import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Collapse,
  Autocomplete
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import ListIcon from '@mui/icons-material/List';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ThumbUpAltTwoToneIcon from '@mui/icons-material/ThumbUpAltTwoTone';
import ThumbDownAltTwoToneIcon from '@mui/icons-material/ThumbDownAltTwoTone';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { apiFetch, openAuthenticatedUpload } from '../api/client';
import API_CONFIG from '../config/api';
import PainelEditalContratacao from '../components/contratos/PainelEditalContratacao';
import MapaComparativoPropostasPanel from '../components/contratos/MapaComparativoPropostasPanel';
import PainelEtapaAprovacaoFluxo from '../components/contratos/PainelEtapaAprovacaoFluxo';
import EditorMinutaClausulas from '../components/contratos/EditorMinutaClausulas';
import { publicarStatusProcessoPrestador } from '../utils/contratosPrestadorSync';
import EditorTRModal from '../components/contratos/EditorTRModal';
import EditorCIModal from '../components/contratos/EditorCIModal';
import EditorNotificacaoModal from '../components/contratos/EditorNotificacaoModal';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import ContratosModuloHeader from '../components/contratos/ContratosModuloHeader';
import AtualizarProcessoModal from '../components/contratos/AtualizarProcessoModal';
import ProcessoRowActions from '../components/contratos/ProcessoRowActions';
import { avaliarPermissaoEtapaProcesso } from './contratos/utils/processoEtapaPermissao';
import ProcessoHistoricoTimeline from '../components/contratos/ProcessoHistoricoTimeline';
import {
  HUD_DIALOG_PAPER,
  HUD_DIALOG_TITLE,
  HUD_DIALOG_CONTENT,
  HUD_CTA_BUTTON,
  HUD_CARD,
  HUD_STEP_NUMBER,
} from '../components/contratos/hudDialogStyles';
import { exportProcessosExcel, exportProcessosPdf } from './contratos/utils/processosExport';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';
import { isUsuarioSetorContratos } from '../utils/contratosDashboardUtils';

const etapaExigeConfiguracaoEdital = (etapa) => {
  if (!etapa) return false;
  if (
    etapa.exigeConfiguracaoEdital === true ||
    etapa.exigeConfiguracaoEdital === 1 ||
    etapa.exige_configuracao_edital === 1 ||
    etapa.exige_configuracao_edital === true
  ) {
    return true;
  }
  return false;
};

/** Intercepta avanço para etapa que exige configuração de edital público. */
const deveInterceptarAvancoEdital = ({ acao, etapaDestino, processo, podePublicar }) =>
  acao === 'APROVAR' &&
  etapaExigeConfiguracaoEdital(etapaDestino) &&
  !processo?.isEditalPublico &&
  Boolean(podePublicar);

const formEditalPublicoPadrao = () => {
  const agora = new Date();
  const em7dias = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000);
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  return {
    dataAbertura: toLocalInput(agora),
    dataEncerramento: toLocalInput(em7dias),
    modoPublico: true,
  };
};

const buscarFluxoPorTipo = (fluxosData, tipoFluxo) => {
  if (!Array.isArray(fluxosData)) return null;
  const tipo = (tipoFluxo || 'CONTRATACAO').toUpperCase();
  if (tipo === 'MINUTA') {
    return fluxosData.find((f) => (f.nome || '').toLowerCase().includes('minuta'));
  }
  if (tipo === 'ADITIVO') {
    return fluxosData.find((f) => (f.nome || '').toLowerCase().includes('aditivo'));
  }
  return fluxosData.find((f) => {
    const n = (f.nome || '').toLowerCase();
    return n.includes('contratação') || n.includes('contratacao');
  });
};

const initialEditalPublicoForm = {
  dataAbertura: '',
  dataEncerramento: '',
  modoPublico: true
};

const STATUS_COLORS = {
  EM_ANDAMENTO: '#1976d2',
  EM_ANALISE: '#0288d1',
  REJEITADO: '#c62828',
  CONCLUIDO: '#2e7d32',
  CANCELADO: '#616161',
  PENDENTE: '#5e35b1'
};

const STATUS_LABELS = {
  EM_ANDAMENTO: 'Em andamento',
  EM_ANALISE: 'Em análise',
  REJEITADO: 'Rejeitado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
  PENDENTE: 'Pendente'
};

const EXTRA_STATUS_COLUMNS = [
  { key: 'REJEITADO', title: 'Rejeitados', color: '#ffcdd2' },
  { key: 'CONCLUIDO', title: 'Concluídos', color: '#c8e6c9' },
  { key: 'CANCELADO', title: 'Cancelados', color: '#eeeeee' }
];

const TERMINAL_STATUSES = ['CONCLUIDO', 'CANCELADO', 'REJEITADO'];

const initialFormProcesso = {
  fluxoId: '',
  titulo: '',
  descricao: '',
  prestadorId: null,
};

const initialMoveForm = {
  etapaDestinoId: '',
  acao: 'APROVAR',
  observacao: ''
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const formatDateShort = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR');
};

// Novo componente de fluxograma visual completo
const FluxogramaVisualCompleto = React.forwardRef(({ fluxo, contagemEtapas, processos, processoSelecionado }, ref) => {
  const [hoveredKey, setHoveredKey] = useState(null);

  const nodes = useMemo(() => {
    if (!fluxo) return [];
    
    // Nó inicial (oval)
    const inicioNode = {
      key: 'inicio',
      id: 'inicio',
      tipo: 'inicio',
      ordem: 0,
      label: 'Início',
      descricao: 'Início do processo',
      responsavel: null,
      status: 'INICIO',
      color: '#e3f2fd',
      borderColor: '#1976d2',
      count: 0,
      processosIds: []
    };

    // Etapas do fluxo (retângulos)
    const etapaNodes = (fluxo.etapas || []).map((etapa, index) => {
      const status = (etapa.status_resultante || 'EM_ANDAMENTO').toUpperCase();
      const processosNaEtapa = processos?.filter(p => p.etapaAtual?.id === etapa.id) || [];
      return {
        key: `etapa-${etapa.id}`,
        id: etapa.id,
        tipo: 'processo',
        ordem: index + 1,
        label: etapa.nome,
        descricao: etapa.descricao,
        responsavel: etapa.responsaveis_ids ? `${Array.isArray(etapa.responsaveis_ids) ? etapa.responsaveis_ids.length : JSON.parse(etapa.responsaveis_ids || '[]').length} responsável(eis)` : null,
        status,
        color: etapa.cor || 'rgba(25,118,210,0.12)',
        borderColor: '#1976d2',
        count: contagemEtapas?.[etapa.id] || processosNaEtapa.length,
        processosIds: processosNaEtapa.map(p => p.id),
        processos: processosNaEtapa
      };
    });

    // Nós terminais (ovais)
    const terminalNodes = TERMINAL_STATUSES.map((status, index) => {
      const processosNoStatus = processos?.filter(p => p.status === status) || [];
      return {
        key: `terminal-${status}`,
        id: status,
        tipo: 'terminal',
        ordem: etapaNodes.length + index + 1,
        label: STATUS_LABELS[status] || status,
        descricao: 'Resultado final do processo.',
        responsavel: null,
        status,
        color: `${STATUS_COLORS[status] || '#90a4ae'}33`,
        borderColor: STATUS_COLORS[status] || '#546e7a',
        count: contagemEtapas?.[status] || processosNoStatus.length,
        processosIds: processosNoStatus.map(p => p.id),
        processos: processosNoStatus
      };
    });

    return [inicioNode, ...etapaNodes, ...terminalNodes];
  }, [fluxo, contagemEtapas, processos]);

  if (!fluxo || nodes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Configure um fluxo para visualizar o fluxograma.
        </Typography>
      </Box>
    );
  }

  // Layout hierárquico em camadas (vertical com ramificações)
  const nodeWidth = 220;
  const nodeHeight = 100;
  const layerSpacing = 180; // Espaçamento vertical entre camadas
  const nodeSpacing = 250; // Espaçamento horizontal entre nós na mesma camada
  const padding = 80;
  
  // Organizar nós em camadas (layers)
  const organizarEmCamadas = () => {
    const layers = [];
    const processed = new Set();
    
    // Camada 0: Início
    const inicio = nodes.find(n => n.tipo === 'inicio');
    if (inicio) {
      layers.push([inicio]);
      processed.add(inicio.key);
    }
    
    // Camadas intermediárias: Etapas do processo
    const etapas = nodes.filter(n => n.tipo === 'processo').sort((a, b) => a.ordem - b.ordem);
    let currentLayer = inicio ? 1 : 0;
    
    // Agrupar etapas em camadas (2-3 etapas por camada para layout mais compacto)
    const etapasPorCamada = 2;
    for (let i = 0; i < etapas.length; i += etapasPorCamada) {
      const layerEtapas = etapas.slice(i, i + etapasPorCamada);
      if (layers.length <= currentLayer) {
        layers.push([]);
      }
      layers[currentLayer].push(...layerEtapas);
      layerEtapas.forEach(e => processed.add(e.key));
      currentLayer++;
    }
    
    // Última camada: Terminais
    const terminais = nodes.filter(n => n.tipo === 'terminal');
    if (terminais.length > 0) {
      // Distribuir terminais em camadas finais
      const terminaisPorCamada = 3;
      for (let i = 0; i < terminais.length; i += terminaisPorCamada) {
        const layerTerminais = terminais.slice(i, i + terminaisPorCamada);
        if (layers.length <= currentLayer) {
          layers.push([]);
        }
        layers[currentLayer].push(...layerTerminais);
        layerTerminais.forEach(t => processed.add(t.key));
        currentLayer++;
      }
    }
    
    return layers;
  };
  
  const layers = organizarEmCamadas();
  
  // Calcular posições dos nós
  const nodesWithPosition = [];
  let maxWidth = 0;
  
  layers.forEach((layer, layerIndex) => {
    const layerWidth = layer.length * nodeSpacing + (layer.length - 1) * 50;
    maxWidth = Math.max(maxWidth, layerWidth);
    
    layer.forEach((node, nodeIndex) => {
      const x = padding + (maxWidth - layerWidth) / 2 + nodeIndex * (nodeSpacing + 50);
      const y = padding + layerIndex * layerSpacing;
      
      nodesWithPosition.push({
        ...node,
        x,
        y,
        layer: layerIndex,
        positionInLayer: nodeIndex
      });
    });
  });
  
  // Calcular dimensões do SVG
  const svgWidth = Math.max(maxWidth + padding * 2, 1000);
  const svgHeight = layers.length * layerSpacing + padding * 2;

  // Verificar se processo selecionado está nesta etapa
  const processoEstaNaEtapa = (node) => {
    if (!processoSelecionado) return false;
    if (node.tipo === 'inicio') return false;
    if (node.tipo === 'terminal') {
      return processoSelecionado.status === node.id;
    }
    return processoSelecionado.etapaAtual?.id === node.id;
  };

  return (
    <svg
      ref={ref}
      width="100%"
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      style={{ backgroundColor: '#fafafa' }}
    >
      <defs>
        <marker id="flux-arrow-green" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#2e7d32" />
        </marker>
        <marker id="flux-arrow-blue" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#1565c0" />
        </marker>
        <marker id="flux-arrow-red" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#c62828" />
        </marker>
      </defs>

      {/* Conectores entre nós - layout hierárquico */}
      {nodesWithPosition.map((node, index) => {
        // Conectar para o próximo nó na sequência
        let nextNode = null;
        
        if (node.tipo === 'inicio') {
          // Início conecta com primeira etapa
          nextNode = nodesWithPosition.find(n => n.tipo === 'processo' && n.ordem === 1);
        } else if (node.tipo === 'processo') {
          // Etapa conecta com próxima etapa ou terminal
          const nextOrdem = node.ordem + 1;
          const nextEtapa = nodesWithPosition.find(n => n.tipo === 'processo' && n.ordem === nextOrdem);
          if (nextEtapa) {
            nextNode = nextEtapa;
          } else {
            // Se não há próxima etapa, conecta com terminais
            nextNode = nodesWithPosition.find(n => n.tipo === 'terminal' && n.status === node.status);
          }
        }
        
        if (!nextNode) return null;
        
        const startX = node.x + nodeWidth / 2;
        const startY = node.y + nodeHeight;
        const endX = nextNode.x + nodeWidth / 2;
        const endY = nextNode.y;
        
        // Criar caminho curvo ou reto dependendo da posição
        const isTerminal = node.tipo === 'terminal';
        const strokeColor = isTerminal ? '#c62828' : '#1565c0';
        const markerId = isTerminal ? 'flux-arrow-red' : 'flux-arrow-blue';
        
        // Se estão na mesma camada ou próximos, usar linha reta
        // Se há diferença de camada, usar caminho curvo
        const deltaY = endY - startY;
        const deltaX = endX - startX;
        const useCurve = Math.abs(deltaY) > 50 || Math.abs(deltaX) > nodeSpacing;
        
        if (useCurve) {
          // Caminho curvo com pontos de controle
          const midY = startY + deltaY / 2;
          const path = `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
          
          return (
            <path
              key={`connector-${node.key}-${nextNode.key}`}
              d={path}
              fill="none"
              stroke={strokeColor}
              strokeWidth={2.5}
              markerEnd={`url(#${markerId})`}
              strokeDasharray={isTerminal ? '5 5' : '0'}
            />
          );
        } else {
          // Linha reta
          return (
            <line
              key={`connector-${node.key}-${nextNode.key}`}
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke={strokeColor}
              strokeWidth={2.5}
              markerEnd={`url(#${markerId})`}
              strokeDasharray={isTerminal ? '5 5' : '0'}
            />
          );
        }
      })}

      {/* Renderizar nós */}
      {nodesWithPosition.map((node) => {
        const isHovered = hoveredKey === node.key;
        const hasProcessos = node.count > 0;
        const processoAqui = processoEstaNaEtapa(node);
        
        let fillColor, strokeColor, shapeElement;
        
        if (node.tipo === 'inicio') {
          // OVAL para início
          fillColor = processoAqui ? '#c8e6c9' : '#e3f2fd';
          strokeColor = processoAqui ? '#2e7d32' : '#1976d2';
          const rx = nodeWidth / 2;
          const ry = nodeHeight / 2;
          shapeElement = (
            <ellipse
              cx={nodeWidth / 2}
              cy={nodeHeight / 2}
              rx={rx}
              ry={ry}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={isHovered ? 3 : 2}
            />
          );
        } else if (node.tipo === 'terminal') {
          // OVAL para terminais
          fillColor = processoAqui ? node.color : node.color;
          strokeColor = processoAqui ? '#2e7d32' : (node.borderColor || '#546e7a');
          const rx = nodeWidth / 2;
          const ry = nodeHeight / 2;
          shapeElement = (
            <ellipse
              cx={nodeWidth / 2}
              cy={nodeHeight / 2}
              rx={rx}
              ry={ry}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={isHovered ? 3 : 2}
            />
          );
        } else {
          // RETÂNGULO para processos
          fillColor = processoAqui 
            ? 'rgba(46,125,50,0.2)' 
            : (isHovered ? 'rgba(25,118,210,0.22)' : (node.color || 'rgba(25,118,210,0.12)'));
          strokeColor = processoAqui 
            ? '#2e7d32' 
            : (hasProcessos ? '#0d47a1' : 'rgba(13,71,161,0.28)');
          shapeElement = (
            <rect
              x={0}
              y={0}
              width={nodeWidth}
              height={nodeHeight}
              rx={8}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={isHovered || processoAqui ? 3 : 1.5}
            />
          );
        }

        const statusLabel = STATUS_LABELS[node.status] || node.status;
        const responsavelLabel = node.responsavel ? `Responsável: ${node.responsavel}` : null;

        return (
          <g
            key={node.key}
            transform={`translate(${node.x}, ${node.y})`}
            onMouseEnter={() => setHoveredKey(node.key)}
            onMouseLeave={() => setHoveredKey(null)}
            style={{ cursor: 'pointer' }}
          >
            {shapeElement}
            
            {/* Número da etapa */}
            {node.ordem > 0 && node.tipo !== 'terminal' && (
              <text
                x={nodeWidth / 2}
                y={22}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill={processoAqui ? '#2e7d32' : '#0d47a1'}
              >
                {node.ordem}. {node.label}
              </text>
            )}
            {node.ordem === 0 && (
              <text
                x={nodeWidth / 2}
                y={nodeHeight / 2 + 4}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill={processoAqui ? '#2e7d32' : '#0d47a1'}
              >
                {node.label}
              </text>
            )}
            {node.tipo === 'terminal' && (
              <text
                x={nodeWidth / 2}
                y={nodeHeight / 2 + 4}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill={processoAqui ? '#2e7d32' : (node.borderColor || '#546e7a')}
              >
                {node.label}
              </text>
            )}
            
            {/* Responsável */}
            {responsavelLabel && node.ordem > 0 && node.tipo !== 'terminal' && (
              <text
                x={nodeWidth / 2}
                y={40}
                textAnchor="middle"
                fontSize={10}
                fill="#1a237e"
              >
                {responsavelLabel}
              </text>
            )}
            
            {/* Status */}
            {node.ordem > 0 && node.tipo !== 'terminal' && (
              <text
                x={nodeWidth / 2}
                y={responsavelLabel ? 56 : 48}
                textAnchor="middle"
                fontSize={9}
                fill="#455a64"
              >
                {statusLabel}
              </text>
            )}
            
            {/* Contador de processos */}
            {node.ordem > 0 && node.tipo !== 'terminal' && (
              <g transform={`translate(${(nodeWidth - 110) / 2}, ${nodeHeight - 28})`}>
                <rect
                  width={110}
                  height={24}
                  rx={12}
                  fill={hasProcessos || processoAqui ? '#1565c0' : '#e3f2fd'}
                  stroke={hasProcessos || processoAqui ? '#0d47a1' : 'transparent'}
                  strokeWidth={1.5}
                />
                <text
                  x={55}
                  y={16}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  fill={hasProcessos || processoAqui ? '#ffffff' : '#0d47a1'}
                >
                  {processoSelecionado && processoAqui 
                    ? '✓ AQUI' 
                    : `${node.count} processo${node.count === 1 ? '' : 's'}`}
                </text>
              </g>
            )}
            {/* Contador para terminais */}
            {node.tipo === 'terminal' && (
              <g transform={`translate(${(nodeWidth - 100) / 2}, ${nodeHeight - 24})`}>
                <rect
                  width={100}
                  height={22}
                  rx={11}
                  fill={hasProcessos || processoAqui ? node.borderColor || '#546e7a' : node.color}
                  stroke={hasProcessos || processoAqui ? '#2e7d32' : (node.borderColor || '#546e7a')}
                  strokeWidth={1.5}
                />
                <text
                  x={50}
                  y={15}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  fill={hasProcessos || processoAqui ? '#ffffff' : '#424242'}
                >
                  {processoSelecionado && processoAqui 
                    ? '✓ AQUI' 
                    : `${node.count} processo${node.count === 1 ? '' : 's'}`}
                </text>
              </g>
            )}
            
            {/* Indicador de processo selecionado */}
            {processoAqui && (
              <>
                <circle
                  cx={nodeWidth - 12}
                  cy={12}
                  r={10}
                  fill="#2e7d32"
                  stroke="#fff"
                  strokeWidth={2.5}
                />
                <text
                  x={nodeWidth - 12}
                  y={16}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  fill="#fff"
                >
                  ✓
                </text>
              </>
            )}
            
            {/* Tooltip com informações */}
            <title>
              {node.label}
              {node.descricao ? `\n${node.descricao}` : ''}
              {node.responsavel ? `\nResponsável: ${node.responsavel}` : ''}
              {node.count > 0 ? `\n${node.count} processo(s) nesta etapa` : ''}
              {processoAqui && processoSelecionado ? `\nProcesso ${processoSelecionado.codigo} está aqui` : ''}
            </title>
          </g>
        );
      })}
    </svg>
  );
});

// Manter o componente antigo para compatibilidade (pode ser usado por outros módulos)
// eslint-disable-next-line no-unused-vars
const FluxogramaVisual = React.forwardRef(({ fluxo, contagemEtapas }, ref) => {
  return <FluxogramaVisualCompleto ref={ref} fluxo={fluxo} contagemEtapas={contagemEtapas} processos={[]} processoSelecionado={null} />;
});

export default function ContratosProcessoContratacaoPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada, user, isAdmin, hasPermission, canAuthorize, rbacRole, fiscalSetor } = useAuth();
  const usuarioEmail = user?.email || '';
  const usuarioTipo = user?.tipo || '';
  const usuarioSetor = user?.setor || '';
  const scopeOpts = { user, rbacRole, fiscalSetor };
  const [fluxos, setFluxos] = useState([]);
  const [fluxoSelecionado, setFluxoSelecionado] = useState('');
  /** Lista isolada — nunca filtra pelo fluxo da aba Indicadores/Fluxos */
  const [processosLista, setProcessosLista] = useState([]);
  /** Indicadores + diagramas — escopo do fluxoSelecionado */
  const [processosIndicadores, setProcessosIndicadores] = useState([]);
  const [loadingFluxos, setLoadingFluxos] = useState(true);
  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingIndicadores, setLoadingIndicadores] = useState(false);
  const [dialogProcessoOpen, setDialogProcessoOpen] = useState(false);
  const [dialogMoverOpen, setDialogMoverOpen] = useState(false);
  const [dialogConfiguracaoOpen, setDialogConfiguracaoOpen] = useState(false);
  const [dialogEditarTROpen, setDialogEditarTROpen] = useState(false);
  const [dialogEditarCIOpen, setDialogEditarCIOpen] = useState(false);
  const [dialogEditarNotificacaoOpen, setDialogEditarNotificacaoOpen] = useState(false);
  const [dialogMinutaProcesso, setDialogMinutaProcesso] = useState({ open: false, processoId: null, titulo: '' });
  const [dialogEditalPublicoOpen, setDialogEditalPublicoOpen] = useState(false);
  const [formEditalPublico, setFormEditalPublico] = useState(initialEditalPublicoForm);
  const [processoSelecionado, setProcessoSelecionado] = useState(null);
  const [fluxoMoverEtapas, setFluxoMoverEtapas] = useState(null);
  const [formProcesso, setFormProcesso] = useState(initialFormProcesso);
  const [formMover, setFormMover] = useState(initialMoveForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState('lista');
  const loadingProcessos = tabValue === 'lista' ? loadingLista : loadingIndicadores;
  const [processoExcluirConfirm, setProcessoExcluirConfirm] = useState(null);
  const [processoReabrirConfirm, setProcessoReabrirConfirm] = useState(null);
  const [formProcessoAttempted, setFormProcessoAttempted] = useState(false);
  const [filtroTipoFluxo, setFiltroTipoFluxo] = useState('');
  const [filtroEtapaId, setFiltroEtapaId] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [processoDetalheId, setProcessoDetalheId] = useState(null);
  const [excluindoProcessoId, setExcluindoProcessoId] = useState(null);
  const [reabrindoProcessoId, setReabrindoProcessoId] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [etapaExpandidaId, setEtapaExpandidaId] = useState(null);
  const [etapaDetalheOpen, setEtapaDetalheOpen] = useState(false);
  const [etapaDetalheData, setEtapaDetalheData] = useState(null);
  const [loadingEtapaDetalhe, setLoadingEtapaDetalhe] = useState(false);
  const fluxogramaRef = useRef(null);
  const anexoInputRef = useRef(null);
  const [anexoFiles, setAnexoFiles] = useState([]);
  const [anexoName, setAnexoName] = useState('');
  const [editandoFluxo, setEditandoFluxo] = useState(false);
  const [fluxoEditado, setFluxoEditado] = useState(null);
  const [etapaEditando, setEtapaEditando] = useState(null);
  const [dialogEtapaOpen, setDialogEtapaOpen] = useState(false);
  const [visualizacaoFluxo, setVisualizacaoFluxo] = useState('agregado'); // 'agregado' ou 'individual'
  const [processoFluxoSelecionado, setProcessoFluxoSelecionado] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [prestadoresOpcoes, setPrestadoresOpcoes] = useState([]);
  const [loadingPrestadores, setLoadingPrestadores] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [setores, setSetores] = useState([]);
  const [loadingSetores, setLoadingSetores] = useState(false);
  const [formEtapa, setFormEtapa] = useState({
    nome: '',
    descricao: '',
    grupo: '',
    responsaveis: [], // Array de IDs de usuários
    prazo: '',
    tempoEstimado: '', // Tempo estimado em dias
    status: 'EM_ANDAMENTO',
    cor: '',
    permiteAprovar: true,
    permiteRejeitar: true,
    permiteDevolver: true,
    retornaInicio: false,
    exigeConfiguracaoEdital: false
  });

  const podeGerenciarProcessos = useMemo(() => {
    if (isAdmin) return true;
    const tipoUpper = (usuarioTipo || '').toUpperCase();
    return tipoUpper.includes('GERENCIA') || tipoUpper.includes('GERENTE') || tipoUpper.includes('GESTOR');
  }, [isAdmin, usuarioTipo]);

  const podeEditarMinutaProcesso = useMemo(
    () =>
      isUsuarioSetorContratos({ user, isAdmin }) ||
      rbacRole === ROLES.SUPER_ADMIN ||
      rbacRole === ROLES.LOCAL_ADMIN,
    [user, isAdmin, rbacRole]
  );

  const abrirMinutaProcesso = (processo) => {
    if (!processo?.id) return;
    setDialogMinutaProcesso({
      open: true,
      processoId: processo.id,
      titulo: processo.titulo || processo.codigo || 'MINUTA DE CONTRATAÇÃO',
    });
  };
  // RBAC: configuração de fluxo/etapa restrita a gestão
  const podeConfigurarFluxo = useMemo(
    () =>
      isAdmin ||
      rbacRole === ROLES.SUPER_ADMIN ||
      rbacRole === ROLES.LOCAL_ADMIN,
    [isAdmin, rbacRole]
  );

  const carregarFluxos = async () => {
    setLoadingFluxos(true);
    try {
      const res = await apiFetch('/api/contratos/fluxos');
      if (!res.ok) throw new Error('Erro ao carregar fluxos');
      const data = await res.json();
      setFluxos(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0) {
        setFluxoSelecionado((prev) => prev || data[0].id);
      }
    } catch (error) {
      console.error(error);
      setFluxos([]);
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar fluxos', severity: 'error' });
    } finally {
      setLoadingFluxos(false);
    }
  };

  const carregarProcessosLista = useCallback(async () => {
    if (!usuarioEmail && !usuarioTipo && !usuarioSetor) {
      setProcessosLista([]);
      return;
    }
    setLoadingLista(true);
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) {
        params.append('unidade', unidadeSelecionada.codigo);
      }
      if (usuarioEmail) params.append('usuarioEmail', usuarioEmail);
      if (usuarioTipo) params.append('usuarioTipo', usuarioTipo);
      if (usuarioSetor) params.append('usuarioSetor', usuarioSetor);
      if (filtroTipoFluxo) params.append('tipoFluxo', filtroTipoFluxo);
      if (filtroEtapaId) params.append('etapaId', filtroEtapaId);
      if (filtroStatus) params.append('status', filtroStatus);
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) {
        params.append('setor', setorScope);
      }
      const path = withSetorScope(`/api/contratos/processos?${params.toString()}`, scopeOpts);
      const res = await apiFetch(path);
      if (!res.ok) throw new Error('Erro ao carregar processos');
      const data = await res.json();
      setProcessosLista(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setProcessosLista([]);
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar processos', severity: 'error' });
    } finally {
      setLoadingLista(false);
    }
  }, [unidadeSelecionada?.codigo, usuarioEmail, usuarioTipo, usuarioSetor, filtroTipoFluxo, filtroEtapaId, filtroStatus, user, rbacRole, fiscalSetor]);

  const carregarProcessosIndicadores = useCallback(async () => {
    if (!fluxoSelecionado) {
      setProcessosIndicadores([]);
      return;
    }
    if (!usuarioEmail && !usuarioTipo && !usuarioSetor) {
      setProcessosIndicadores([]);
      return;
    }
    setLoadingIndicadores(true);
    try {
      const params = new URLSearchParams();
      params.append('fluxoId', fluxoSelecionado);
      if (unidadeSelecionada?.codigo) {
        params.append('unidade', unidadeSelecionada.codigo);
      }
      if (usuarioEmail) params.append('usuarioEmail', usuarioEmail);
      if (usuarioTipo) params.append('usuarioTipo', usuarioTipo);
      if (usuarioSetor) params.append('usuarioSetor', usuarioSetor);
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) {
        params.append('setor', setorScope);
      }
      const path = withSetorScope(`/api/contratos/processos?${params.toString()}`, scopeOpts);
      const res = await apiFetch(path);
      if (!res.ok) throw new Error('Erro ao carregar processos');
      const data = await res.json();
      setProcessosIndicadores(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setProcessosIndicadores([]);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao carregar indicadores',
        severity: 'error',
      });
    } finally {
      setLoadingIndicadores(false);
    }
  }, [fluxoSelecionado, unidadeSelecionada?.codigo, usuarioEmail, usuarioTipo, usuarioSetor, user, rbacRole, fiscalSetor]);

  const carregarProcessos = useCallback(async () => {
    if (tabValue === 'lista') return carregarProcessosLista();
    return carregarProcessosIndicadores();
  }, [tabValue, carregarProcessosLista, carregarProcessosIndicadores]);

  const refreshProcessosAposMutacao = useCallback(() => {
    carregarProcessosLista();
    if (fluxoSelecionado) carregarProcessosIndicadores();
  }, [carregarProcessosLista, carregarProcessosIndicadores, fluxoSelecionado]);

  const findProcesso = useCallback(
    (id) => {
      if (id == null) return null;
      const n = Number(id);
      return (
        processosLista.find((p) => p.id === id || p.id === n) ||
        processosIndicadores.find((p) => p.id === id || p.id === n) ||
        null
      );
    },
    [processosLista, processosIndicadores]
  );

  const patchProcessoLocal = useCallback((id, data) => {
    const apply = (prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...data } : item));
    setProcessosLista(apply);
    setProcessosIndicadores(apply);
  }, []);

  const loadTimeline = useCallback((processoId) => {
    if (!processoId) return;
    setLoadingTimeline(true);
    setTimelineData(null);
    apiFetch(`/api/contratos/processos/${processoId}/timeline`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { setTimelineData(data); setLoadingTimeline(false); })
      .catch(() => setLoadingTimeline(false));
  }, []);

  // Carregar setores e usuários para o modal Editar Etapa (Setor/Grupo e Responsáveis)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingSetores(true);
      setLoadingUsuarios(true);
      try {
        const [resSetores, resUsuarios] = await Promise.all([
          apiFetch('/api/setores'),
          apiFetch('/api/usuarios')
        ]);
        if (cancelled) return;
        if (resSetores.ok) {
          const data = await resSetores.json();
          setSetores(Array.isArray(data) ? data : []);
        }
        if (resUsuarios.ok) {
          const data = await resUsuarios.json();
          setUsuarios(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) console.error('Erro ao carregar setores/usuários:', e);
      } finally {
        if (!cancelled) {
          setLoadingSetores(false);
          setLoadingUsuarios(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const carregarPrestadoresOpcoes = useCallback(async () => {
    setLoadingPrestadores(true);
    try {
      const res = await apiFetch('/api/prestadores');
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || 'Erro ao carregar prestadores');
      setPrestadoresOpcoes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setPrestadoresOpcoes([]);
    } finally {
      setLoadingPrestadores(false);
    }
  }, []);

  const fluxoFormEhAditivo = useMemo(() => {
    const f = fluxos.find((x) => Number(x.id) === Number(formProcesso.fluxoId));
    return (f?.nome || '').toLowerCase().includes('aditivo');
  }, [fluxos, formProcesso.fluxoId]);

  const prestadorFormSelecionado = useMemo(
    () => prestadoresOpcoes.find((p) => Number(p.id) === Number(formProcesso.prestadorId)) || null,
    [prestadoresOpcoes, formProcesso.prestadorId]
  );

  useEffect(() => {
    if (processoDetalheId) {
      setEtapaExpandidaId(null);
      loadTimeline(processoDetalheId);
    } else {
      setTimelineData(null);
    }
  }, [processoDetalheId, loadTimeline]);

  useEffect(() => {
    carregarFluxos();
  }, []);

  useEffect(() => {
    if (!(usuarioEmail || usuarioTipo || usuarioSetor)) return;
    carregarProcessosLista();
  }, [usuarioEmail, usuarioTipo, usuarioSetor, carregarProcessosLista]);

  useEffect(() => {
    if (!(usuarioEmail || usuarioTipo || usuarioSetor)) return;
    if (tabValue === 'processos' || tabValue === 'fluxos') {
      carregarProcessosIndicadores();
    }
  }, [tabValue, fluxoSelecionado, usuarioEmail, usuarioTipo, usuarioSetor, carregarProcessosIndicadores]);

  useEffect(() => {
    const handleRefresh = (event) => {
      if (event?.detail?.source === 'processos') return;
      if (tabValue === 'lista') carregarProcessosLista();
      else if (fluxoSelecionado) carregarProcessosIndicadores();
    };
    window.addEventListener('contratos:refresh', handleRefresh);
    return () => window.removeEventListener('contratos:refresh', handleRefresh);
  }, [carregarProcessosLista, carregarProcessosIndicadores, fluxoSelecionado, tabValue]);

  const fluxoAtual = useMemo(
    () => fluxos.find((fluxo) => fluxo.id === fluxoSelecionado) || null,
    [fluxos, fluxoSelecionado]
  );

  /** Etapas do filtro da Lista: conforme tipo selecionado, ou união de todos os fluxos. */
  const etapasFiltroLista = useMemo(() => {
    if (filtroTipoFluxo) {
      const fluxoTipo = buscarFluxoPorTipo(fluxos, filtroTipoFluxo);
      return fluxoTipo?.etapas || [];
    }
    const mapa = new Map();
    (fluxos || []).forEach((f) => {
      (f.etapas || []).forEach((et) => {
        if (et?.id != null && !mapa.has(et.id)) mapa.set(et.id, et);
      });
    });
    return Array.from(mapa.values());
  }, [fluxos, filtroTipoFluxo]);

  const resumoProcessos = useMemo(() => {
    const total = processosIndicadores.length;
    const aguardando = processosIndicadores.filter((p) =>
      ['PENDENTE', 'EM_ANALISE'].includes(p.status)
    ).length;
    const emAndamento = processosIndicadores.filter((p) => p.status === 'EM_ANDAMENTO').length;
    const concluidos = processosIndicadores.filter((p) => p.status === 'CONCLUIDO').length;
    const cancelados = processosIndicadores.filter((p) =>
      ['CANCELADO', 'REJEITADO'].includes(p.status)
    ).length;

    return [
      {
        titulo: 'Total de processos',
        valor: total,
        cor: '#1565c0',
        fundo: 'linear-gradient(135deg, rgba(21,101,192,0.12) 0%, rgba(144,202,249,0.18) 100%)',
        icone: <TimelineIcon sx={{ fontSize: 26 }} />
      },
      {
        titulo: 'Em andamento',
        valor: emAndamento,
        cor: '#ff8f00',
        fundo: 'linear-gradient(135deg, rgba(255,143,0,0.12) 0%, rgba(255,183,77,0.18) 100%)',
        icone: <PendingActionsIcon sx={{ fontSize: 26 }} />
      },
      {
        titulo: 'Aguardando aprovação',
        valor: aguardando,
        cor: '#7b1fa2',
        fundo: 'linear-gradient(135deg, rgba(123,31,162,0.12) 0%, rgba(186,104,200,0.18) 100%)',
        icone: <AssignmentTurnedInIcon sx={{ fontSize: 26 }} />
      },
      {
        titulo: 'Concluídos',
        valor: concluidos,
        cor: '#2e7d32',
        fundo: 'linear-gradient(135deg, rgba(46,125,50,0.12) 0%, rgba(102,187,106,0.18) 100%)',
        icone: <DoneAllIcon sx={{ fontSize: 26 }} />
      },
      {
        titulo: 'Cancelados/Rejeitados',
        valor: cancelados,
        cor: '#c62828',
        fundo: 'linear-gradient(135deg, rgba(198,40,40,0.12) 0%, rgba(239,83,80,0.18) 100%)',
        icone: <CancelPresentationIcon sx={{ fontSize: 26 }} />
      }
    ];
  }, [processosIndicadores]);

  const contagemEtapas = useMemo(() => {
    const mapa = {};
    processosIndicadores.forEach((processo) => {
      if (processo.etapaAtual?.id) {
        mapa[processo.etapaAtual.id] = (mapa[processo.etapaAtual.id] || 0) + 1;
      } else if (TERMINAL_STATUSES.includes(processo.status)) {
        mapa[processo.status] = (mapa[processo.status] || 0) + 1;
      }
    });
    return mapa;
  }, [processosIndicadores]);

  const handleOpenDialogProcesso = () => {
    setFormProcesso((prev) => ({
      ...prev,
      // Lista: usuário escolhe o fluxo. Indicadores/Fluxos: pré-preenche o fluxo da aba.
      fluxoId: tabValue === 'lista' ? '' : fluxoSelecionado || '',
      prestadorId: null,
    }));
    setDialogProcessoOpen(true);
    carregarPrestadoresOpcoes();
  };

  const handleCloseDialogProcesso = () => {
    if (!saving) {
      setDialogProcessoOpen(false);
      setFormProcesso(initialFormProcesso);
      setFormProcessoAttempted(false);
    }
  };

  const handleSalvarProcesso = async () => {
    setFormProcessoAttempted(true);
    if (!formProcesso.fluxoId || !formProcesso.titulo.trim()) {
      setSnackbar({ open: true, message: 'Informe o fluxo e o título do processo', severity: 'warning' });
      return;
    }
    if (fluxoFormEhAditivo && !formProcesso.prestadorId) {
      setSnackbar({
        open: true,
        message: 'Aditivo exige vincular um Prestador/Contrato existente.',
        severity: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/contratos/processos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fluxoId: formProcesso.fluxoId,
          titulo: formProcesso.titulo,
          descricao: formProcesso.descricao,
          unidade: unidadeSelecionada?.codigo || null,
          criadoPor: user?.nome || user?.email || null,
          prestadorId: formProcesso.prestadorId || null,
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Erro ao criar processo');
      }

      setSnackbar({ open: true, message: 'Processo criado com sucesso!', severity: 'success' });
      setDialogProcessoOpen(false);
      setFormProcesso(initialFormProcesso);
      refreshProcessosAposMutacao();
      publicarStatusProcessoPrestador({
        processo: data,
        etapaAtual: data?.etapaAtual,
        acao: 'CRIAR',
        origem: 'processos'
      });
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'processos',
            tipo: 'create',
            processoId: data?.id || null
          }
        })
      );
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: error.message || 'Erro ao criar processo', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const resolverFluxoDoProcesso = useCallback(
    (processo) => {
      if (!processo) return null;
      const fid = processo.fluxoId ?? processo.fluxo_id ?? processo.fluxo?.id;
      if (fid != null) {
        const found = fluxos.find((f) => Number(f.id) === Number(fid));
        if (found) return found;
      }
      return buscarFluxoPorTipo(fluxos, processo.tipoFluxo || 'CONTRATACAO');
    },
    [fluxos]
  );

  const handleAbrirDialogMover = (processo) => {
    setProcessoSelecionado(processo);
    setFluxoMoverEtapas(resolverFluxoDoProcesso(processo));
    resetAnexoSelecionado();
    setFormMover(initialMoveForm);
    setDialogMoverOpen(true);
  };

  const handleBeforeMoverSubmit = async ({ processo, etapaDestino, etapaDestinoId, acao, observacao, anexoFile: af, anexoFiles: afs }) => {
    setProcessoSelecionado(processo);
    setFormMover({ etapaDestinoId, acao, observacao });
    const lista = Array.isArray(afs) && afs.length ? afs : af ? [af] : [];
    setAnexoFiles(lista);
    setAnexoName(lista.map((f) => f.name).filter(Boolean).join(', ') || '');

    if (
      deveInterceptarAvancoEdital({
        acao,
        etapaDestino,
        processo,
        podePublicar: canAuthorize?.() || hasPermission?.('Contratos'),
      })
    ) {
      setFormEditalPublico(formEditalPublicoPadrao());
      setDialogEditalPublicoOpen(true);
      return false;
    }
    return true;
  };

  const handleMoverSuccess = ({ data, error, acao: acaoMov }) => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'warning' });
      return;
    }
    if (!data) return;
    let msgMinuta = data.processoMinutaCriado
      ? `Processo concluído. Processo de Minuta criado: ${data.processoMinutaCriado.codigo}. Acesse a aba "Lista de processos" para abrir.`
      : 'Processo atualizado com sucesso!';
    if (data.editalPublicado?.codigo) {
      msgMinuta = `Processo atualizado e edital ${data.editalPublicado.codigo} publicado no portal.`;
    } else if (data.editalPublicacaoErro) {
      msgMinuta = `Processo atualizado, mas a publicação do edital falhou: ${data.editalPublicacaoErro}`;
    }
    setSnackbar({
      open: true,
      message: data.exigeAssinaturaDigital
        ? `${msgMinuta} Esta etapa exige assinatura digital — envie o documento em Assinaturas.`
        : msgMinuta,
      severity: data.editalPublicacaoErro ? 'warning' : 'success'
    });
    setDialogMoverOpen(false);
    setDialogEditalPublicoOpen(false);
    setFormEditalPublico(initialEditalPublicoForm);
    setProcessoSelecionado(null);
    setFormMover(initialMoveForm);
    resetAnexoSelecionado();
    patchProcessoLocal(data.id, data);
    publicarStatusProcessoPrestador({
      processo: data,
      etapaAtual: data?.etapaAtual,
      acao: acaoMov === 'APROVAR' ? 'MOVER' : (acaoMov || formMover.acao === 'APROVAR' ? 'MOVER' : formMover.acao),
      origem: 'processos'
    });
    window.dispatchEvent(
      new CustomEvent('contratos:refresh', {
        detail: {
          source: 'processos',
          tipo: 'update',
          processoId: data?.id,
          etapaId: data?.etapaAtual?.id
        }
      })
    );
    refreshProcessosAposMutacao();
    if (data.exigeAssinaturaDigital && data.assinaturaDocumentoId) {
      navigate(`/assinaturas/documentos/${data.assinaturaDocumentoId}`);
    } else if (data.exigeAssinaturaDigital && data.id) {
      const tipoFluxo = String(data.tipoFluxo || 'CONTRATACAO').toUpperCase();
      navigate('/assinaturas/novo', {
        state: {
          fromContratos: true,
          processoId: data.id,
          titulo: `Assinatura — ${data.codigo || data.titulo || 'Processo'}`,
          descricao: data.descricao || data.etapaAtual?.nome || '',
          processoCodigo: data.codigo || null,
          tipoDocumento: tipoFluxo === 'ADITIVO' ? 'ADITIVO' : tipoFluxo === 'MINUTA' ? 'MINUTA' : 'CONTRATACAO',
          arquivoUrl: data.documentoFinalUrl || null,
        },
      });
    }
  };

  const handleSolicitarExcluirProcesso = (processo, e) => {
    if (e) e.stopPropagation();
    setProcessoExcluirConfirm(processo);
  };

  const handleExcluirProcesso = async (processo) => {
    if (!processo?.id) return;
    setExcluindoProcessoId(processo.id);
    try {
      const params = new URLSearchParams();
      if (usuarioEmail) params.append('usuarioEmail', usuarioEmail);
      if (usuarioTipo) params.append('usuarioTipo', usuarioTipo);
      if (usuarioSetor) params.append('usuarioSetor', usuarioSetor);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await apiFetch(`/api/contratos/processos/${processo.id}${qs}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao excluir processo');
      setSnackbar({ open: true, message: 'Processo excluído com sucesso.', severity: 'success' });
      refreshProcessosAposMutacao();
      if (processoDetalheId === processo.id) setProcessoDetalheId(null);
      window.dispatchEvent(new CustomEvent('contratos:refresh', { detail: { source: 'processos' } }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao excluir processo', severity: 'error' });
    } finally {
      setExcluindoProcessoId(null);
      setProcessoExcluirConfirm(null);
    }
  };

  const handleSolicitarReabrirProcesso = (processo, e) => {
    if (e) e.stopPropagation();
    setProcessoReabrirConfirm(processo);
  };

  const handleReabrirProcesso = async (processo) => {
    if (!processo?.id) return;
    setReabrindoProcessoId(processo.id);
    try {
      const res = await apiFetch(`/api/contratos/processos/${processo.id}/reabrir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          observacao: 'Processo reaberto após rejeição',
          usuario: user?.nome || usuarioEmail || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao reabrir processo');
      setSnackbar({
        open: true,
        message: `Processo ${data.codigo || processo.codigo || ''} reaberto e retornado à primeira etapa.`,
        severity: 'success',
      });
      patchProcessoLocal(data.id, data);
      refreshProcessosAposMutacao();
      if (processoDetalheId === processo.id) {
        loadTimeline(processo.id);
      }
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: { source: 'processos', tipo: 'reabrir', processoId: data?.id },
        })
      );
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao reabrir processo', severity: 'error' });
    } finally {
      setReabrindoProcessoId(null);
      setProcessoReabrirConfirm(null);
    }
  };

  const handleFecharDialogMover = () => {
    if (!saving) {
      setDialogMoverOpen(false);
      setProcessoSelecionado(null);
      setFluxoMoverEtapas(null);
      setFormMover(initialMoveForm);
      resetAnexoSelecionado();
      setDialogEditalPublicoOpen(false);
      setFormEditalPublico(initialEditalPublicoForm);
    }
  };

  /**
   * Executa o PUT /mover. Quando `editalConfig` é informado, anexa flags de publicação.
   * Fluxo ativo: AtualizarProcessoModal → onBeforeSubmit (edital) → ou este helper via dialog edital.
   */
  const executarMoverProcesso = async (editalConfig = null) => {
    if (!processoSelecionado) return;
    const etapaDestinoId = formMover.etapaDestinoId;

    setSaving(true);
    const voltarInicio = formMover.acao === 'REJEITAR';
    try {
      const formData = new FormData();
      formData.append('etapaDestinoId', etapaDestinoId);
      formData.append('acao', formMover.acao === 'APROVAR' ? 'MOVER' : formMover.acao);
      formData.append('observacao', formMover.observacao);
      formData.append('usuario', user?.nome || user?.email || '');
      formData.append('status', '');
      formData.append('voltarInicio', voltarInicio ? 'true' : 'false');
      if (usuarioEmail) formData.append('usuarioEmail', usuarioEmail);
      if (usuarioTipo) formData.append('usuarioTipo', usuarioTipo);
      if (usuarioSetor) formData.append('usuarioSetor', usuarioSetor);
      (anexoFiles || []).forEach((file) => formData.append('anexo', file));

      if (editalConfig) {
        // TODO: API — propagar isEditalPublico + janela de datas para o portal
        formData.append('isEditalPublico', 'true');
        formData.append('dataAberturaEdital', editalConfig.dataAbertura);
        formData.append('dataEncerramentoEdital', editalConfig.dataEncerramento);
        formData.append('editalPublico', editalConfig.modoPublico ? 'true' : 'false');
      }

      const res = await apiFetch(
        `/api/contratos/processos/${processoSelecionado.id}/mover`,
        { method: 'PUT', body: formData }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Erro ao mover processo');
      }

      let msgMinuta = data.processoMinutaCriado
        ? `Processo concluído. Processo de Minuta criado: ${data.processoMinutaCriado.codigo}. Acesse a aba "Lista de processos" para abrir.`
        : 'Processo atualizado com sucesso!';
      if (data.editalPublicado?.codigo) {
        msgMinuta = `Processo atualizado e edital ${data.editalPublicado.codigo} publicado no portal.`;
      } else if (data.editalPublicacaoErro) {
        msgMinuta = `Processo atualizado, mas a publicação do edital falhou: ${data.editalPublicacaoErro}`;
      }
      setSnackbar({
        open: true,
        message: msgMinuta,
        severity: data.editalPublicacaoErro ? 'warning' : 'success'
      });
      setDialogMoverOpen(false);
      setDialogEditalPublicoOpen(false);
      setFormEditalPublico(initialEditalPublicoForm);
      setProcessoSelecionado(null);
      setFormMover(initialMoveForm);
      resetAnexoSelecionado();
      patchProcessoLocal(data.id, data);
      publicarStatusProcessoPrestador({
        processo: data,
        etapaAtual: data?.etapaAtual,
        acao: formMover.acao === 'APROVAR' ? 'MOVER' : formMover.acao,
        origem: 'processos'
      });
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'processos',
            tipo: 'update',
            processoId: data?.id || processoSelecionado.id,
            etapaId: data?.etapaAtual?.id || etapaDestinoId
          }
        })
      );
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: error.message || 'Erro ao mover processo', severity: 'error' });
    } finally {
      setSaving(false);
      refreshProcessosAposMutacao();
    }
  };

  const handleConfirmarEditalPublico = async (editalForm) => {
    const cfg = editalForm || formEditalPublico;
    if (!cfg?.dataAbertura || !cfg?.dataEncerramento) {
      setSnackbar({
        open: true,
        message: 'Informe data/hora de abertura e de encerramento do edital.',
        severity: 'warning'
      });
      return;
    }
    if (new Date(cfg.dataEncerramento) <= new Date(cfg.dataAbertura)) {
      setSnackbar({
        open: true,
        message: 'A data de encerramento deve ser posterior à de abertura.',
        severity: 'warning'
      });
      return;
    }
    if (!canAuthorize?.() && rbacRole !== ROLES.LOCAL_ADMIN && rbacRole !== ROLES.LOCAL_APPROVER) {
      setSnackbar({
        open: true,
        message: 'Sem permissão para publicar edital público.',
        severity: 'error'
      });
      return;
    }
    await executarMoverProcesso({
      dataAbertura: cfg.dataAbertura,
      dataEncerramento: cfg.dataEncerramento,
      modoPublico: cfg.modoPublico !== false
    });
  };

  const handleDownloadFluxograma = useCallback(() => {
    if (!fluxogramaRef.current || !fluxoAtual) {
      setSnackbar({ open: true, message: 'Fluxograma indisponível para download.', severity: 'warning' });
      return;
    }

    try {
      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(fluxogramaRef.current);
      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      if (!source.startsWith('<?xml')) {
        source = `<?xml version="1.0" encoding="UTF-8"?>\n${source}`;
      }

      const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const nomeFluxo = fluxoAtual.nome ? fluxoAtual.nome.toLowerCase().replace(/\s+/g, '-') : 'processo';
      link.download = `fluxograma-${nomeFluxo}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'Fluxograma exportado em SVG.', severity: 'success' });
    } catch (error) {
      console.error('Erro ao exportar fluxograma:', error);
      setSnackbar({ open: true, message: 'Não foi possível exportar o fluxograma.', severity: 'error' });
    }
  }, [fluxoAtual, setSnackbar]);

  const resetAnexoSelecionado = () => {
    setAnexoFiles([]);
    setAnexoName('');
    if (anexoInputRef.current) {
      anexoInputRef.current.value = '';
    }
  };

  const abrirEdicaoFluxo = () => {
    if (!fluxoAtual) return;
    setFluxoEditado({
      id: fluxoAtual.id,
      nome: fluxoAtual.nome || '',
      descricao: fluxoAtual.descricao || '',
      etapas: (fluxoAtual.etapas || []).map((etapa, index) => {
        let responsaveis = [];
        try {
          const r = etapa.responsaveis_ids;
          if (r != null && r !== '') {
            responsaveis = typeof r === 'string' ? JSON.parse(r) : r;
            if (!Array.isArray(responsaveis)) responsaveis = [];
          }
        } catch (_) {}
        return {
          id: etapa.id,
          nome: etapa.nome || '',
          descricao: etapa.descricao || '',
          ordem: etapa.ordem || index + 1,
          grupo: etapa.grupo_responsavel || '',
          responsaveis,
          prazo: etapa.prazo_dias || '',
          tempoEstimado: etapa.tempo_estimado_dias ?? '',
          status: etapa.status_resultante || 'EM_ANDAMENTO',
          cor: etapa.cor || '',
          permiteAprovar: etapa.permite_aprovar !== 0,
          permiteRejeitar: etapa.permite_rejeitar !== 0,
          permiteDevolver: etapa.permite_devolver !== 0,
          retornaInicio: etapa.retorna_inicio_rejeicao === 1,
          exigeConfiguracaoEdital:
            etapa.exigeConfiguracaoEdital === true ||
            Number(etapa.exige_configuracao_edital) === 1
        };
      })
    });
    setEditandoFluxo(true);
  };

  const fecharEdicaoFluxo = () => {
    if (!saving) {
      setEditandoFluxo(false);
      setFluxoEditado(null);
      setEtapaEditando(null);
      setDialogEtapaOpen(false);
    }
  };

  const adicionarEtapa = () => {
    if (!fluxoEditado) return;
    const novaOrdem = fluxoEditado.etapas.length + 1;
    setFormEtapa({
      nome: '',
      descricao: '',
      grupo: '',
      responsaveis: [],
      prazo: '',
      tempoEstimado: '',
      status: 'EM_ANDAMENTO',
      cor: '',
      permiteAprovar: true,
      permiteRejeitar: true,
      permiteDevolver: true,
      retornaInicio: false,
      exigeConfiguracaoEdital: false
    });
    setEtapaEditando({ ordem: novaOrdem, isNew: true });
    setDialogEtapaOpen(true);
  };

  const editarEtapa = (etapa, index) => {
    // Converter responsaveis: aceita .responsaveis (array) ou .responsaveis_ids (JSON string do backend)
    let responsaveisArray = [];
    const rawResponsaveis = etapa.responsaveis ?? etapa.responsaveis_ids;
    if (rawResponsaveis != null) {
      if (Array.isArray(rawResponsaveis)) {
        responsaveisArray = rawResponsaveis.map((id) => (typeof id === 'number' ? id : parseInt(id, 10))).filter((n) => !Number.isNaN(n));
      } else if (typeof rawResponsaveis === 'string' && rawResponsaveis.trim() !== '') {
        try {
          const parsed = JSON.parse(rawResponsaveis);
          responsaveisArray = Array.isArray(parsed) ? parsed.map((id) => (typeof id === 'number' ? id : parseInt(id, 10))).filter((n) => !Number.isNaN(n)) : [];
        } catch {
          responsaveisArray = rawResponsaveis.split(',').filter(Boolean).map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n));
        }
      }
    }
    const grupo = etapa.grupo ?? etapa.grupo_responsavel ?? '';
    const prazo = etapa.prazo ?? etapa.prazo_dias ?? '';
    const tempoEstimado = etapa.tempoEstimado ?? etapa.tempo_estimado_dias ?? '';
    const status = etapa.status ?? etapa.status_resultante ?? 'EM_ANDAMENTO';
    setFormEtapa({
      nome: etapa.nome || '',
      descricao: etapa.descricao || '',
      grupo: typeof grupo === 'string' ? grupo : '',
      responsaveis: responsaveisArray,
      prazo: prazo !== undefined && prazo !== null ? String(prazo) : '',
      tempoEstimado: tempoEstimado !== undefined && tempoEstimado !== null ? String(tempoEstimado) : '',
      status,
      cor: etapa.cor || '',
      permiteAprovar: etapa.permiteAprovar !== false && etapa.permite_aprovar !== 0,
      permiteRejeitar: etapa.permiteRejeitar !== false && etapa.permite_rejeitar !== 0,
      permiteDevolver: etapa.permiteDevolver !== false && etapa.permite_devolver !== 0,
      retornaInicio: etapa.retornaInicio === true || etapa.retorna_inicio_rejeicao === 1,
      exigeConfiguracaoEdital:
        etapa.exigeConfiguracaoEdital === true ||
        Number(etapa.exige_configuracao_edital) === 1
    });
    setEtapaEditando({ index, etapa, isNew: false });
    setDialogEtapaOpen(true);
  };

  const removerEtapa = (index) => {
    if (!fluxoEditado) return;
    const confirmou = window.confirm('Deseja realmente remover esta etapa?');
    if (!confirmou) return;
    const novasEtapas = fluxoEditado.etapas
      .filter((_, i) => i !== index)
      .map((etapa, i) => ({ ...etapa, ordem: i + 1 }));
    setFluxoEditado({ ...fluxoEditado, etapas: novasEtapas });
  };

  const moverEtapa = (index, direcao) => {
    if (!fluxoEditado) return;
    const novasEtapas = [...fluxoEditado.etapas];
    const novoIndex = direcao === 'up' ? index - 1 : index + 1;
    if (novoIndex < 0 || novoIndex >= novasEtapas.length) return;
    [novasEtapas[index], novasEtapas[novoIndex]] = [novasEtapas[novoIndex], novasEtapas[index]];
    novasEtapas.forEach((etapa, i) => {
      etapa.ordem = i + 1;
    });
    setFluxoEditado({ ...fluxoEditado, etapas: novasEtapas });
  };

  const salvarEtapa = () => {
    if (!fluxoEditado || !etapaEditando) return;
    if (!formEtapa.nome.trim()) {
      setSnackbar({ open: true, message: 'Informe o nome da etapa', severity: 'warning' });
      return;
    }

    const novasEtapas = [...fluxoEditado.etapas];
    const etapaAtualizada = {
      ...formEtapa,
      ordem: etapaEditando.ordem || etapaEditando.index + 1,
      id: etapaEditando.isNew ? null : (etapaEditando.etapa?.id || null)
    };

    if (etapaEditando.isNew) {
      novasEtapas.push(etapaAtualizada);
    } else {
      novasEtapas[etapaEditando.index] = etapaAtualizada;
    }

    novasEtapas.forEach((etapa, i) => {
      etapa.ordem = i + 1;
    });

    setFluxoEditado({ ...fluxoEditado, etapas: novasEtapas });
    setDialogEtapaOpen(false);
    setEtapaEditando(null);
  };

  const salvarFluxo = async () => {
    if (!fluxoEditado) return;
    if (!fluxoEditado.nome.trim()) {
      setSnackbar({ open: true, message: 'Informe o nome do fluxo', severity: 'warning' });
      return;
    }
    if (fluxoEditado.etapas.length === 0) {
      setSnackbar({ open: true, message: 'Adicione ao menos uma etapa', severity: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: fluxoEditado.nome.trim(),
        descricao: fluxoEditado.descricao?.trim() || null,
        etapas: fluxoEditado.etapas.map((etapa) => ({
          nome: etapa.nome.trim(),
          descricao: etapa.descricao?.trim() || null,
          ordem: etapa.ordem,
          grupo: etapa.grupo || null,
          responsaveis: Array.isArray(etapa.responsaveis) ? etapa.responsaveis : [],
          prazo: etapa.prazo ? parseInt(etapa.prazo, 10) : null,
          tempoEstimado: etapa.tempoEstimado != null && etapa.tempoEstimado !== '' ? parseInt(etapa.tempoEstimado, 10) : null,
          status: etapa.status || 'EM_ANDAMENTO',
          cor: etapa.cor || null,
          permiteAprovar: etapa.permiteAprovar ? 1 : 0,
          permiteRejeitar: etapa.permiteRejeitar ? 1 : 0,
          permiteDevolver: etapa.permiteDevolver ? 1 : 0,
          retornaInicio: etapa.retornaInicio ? 1 : 0,
          exigeConfiguracaoEdital: etapa.exigeConfiguracaoEdital ? 1 : 0
        }))
      };
      const response = await apiFetch(`/api/contratos/fluxos/${fluxoEditado.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao salvar fluxo');
      }

      setSnackbar({ open: true, message: 'Fluxo atualizado com sucesso!', severity: 'success' });
      const fluxoIdSalvo = fluxoEditado.id;
      setEditandoFluxo(false);
      setFluxoEditado(null);
      await carregarFluxos();
      if (fluxoSelecionado === fluxoIdSalvo) {
        refreshProcessosAposMutacao();
      }
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar fluxo', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <VisaoRestritaBanner />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ContratosModuloHeader />
      </Box>
      {/* Modais de detalhe (sempre renderizados para abrir em qualquer aba) */}
      <Dialog
        open={Boolean(processoDetalheId)}
        onClose={() => { setProcessoDetalheId(null); setTimelineData(null); setEtapaDetalheOpen(false); }}
        maxWidth="md"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            background: 'linear-gradient(180deg, #f1f5f9 0%, #ffffff 18%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            background:
              'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,64,175,0.9) 55%, rgba(13,148,136,0.82) 100%)',
            color: '#e2e8f0',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              Processo {findProcesso(processoDetalheId)?.codigo || processoDetalheId}
            </Typography>
            {(() => {
              const pDet = findProcesso(processoDetalheId);
              if (pDet?.prestador?.empresa || pDet?.prestador?.numeroContrato) {
                return (
                  <Chip
                    size="small"
                    label={`Prestador: ${pDet.prestador.empresa || pDet.prestador.numeroContrato}`}
                    sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.16)', color: '#fff' }}
                  />
                );
              }
              if (pDet?.status === 'CONCLUIDO' && !pDet?.prestadorId && podeGerenciarProcessos) {
                return (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      navigate('/prestadores', {
                        state: {
                          fromProcesso: {
                            id: pDet.id,
                            codigo: pDet.codigo,
                            titulo: pDet.titulo,
                            unidade: pDet.unidade,
                          },
                        },
                      })
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#fef08a',
                      borderColor: 'rgba(254,240,138,0.5)',
                    }}
                  >
                    Criar prestador a partir deste processo
                  </Button>
                );
              }
              return null;
            })()}
            {timelineData?.timeline && (() => {
              const atual = timelineData.timeline.find((e) => e.ehAtual);
              return atual ? (
                <Chip
                  size="small"
                  label={`Agora: ${atual.nome}`}
                  sx={{ fontWeight: 700, bgcolor: 'rgba(45,212,191,0.22)', color: '#ccfbf1' }}
                />
              ) : null;
            })()}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => processoDetalheId && loadTimeline(processoDetalheId)}
              disabled={loadingTimeline}
              sx={{ textTransform: 'none', color: '#e0f2fe', borderColor: 'rgba(224,242,254,0.4)' }}
            >
              Atualizar
            </Button>
            {processoDetalheId && (() => {
              const pDetalhe = findProcesso(processoDetalheId);
              const isMinuta =
                String(pDetalhe?.tipoFluxo || '').toUpperCase() === 'MINUTA' ||
                String(pDetalhe?.etapaAtual?.tipoAcao || '').toUpperCase() === 'MINUTA_CLAUSULAS';
              if (!isMinuta) return null;
              return (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => abrirMinutaProcesso(pDetalhe)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {podeEditarMinutaProcesso ? 'Editar Minuta' : 'Ver Minuta'}
                </Button>
              );
            })()}
            {processoDetalheId && (() => {
              const pDetalhe = findProcesso(processoDetalheId);
              const rejeitado = String(pDetalhe?.status || '').toUpperCase() === 'REJEITADO';
              if (rejeitado && (podeGerenciarProcessos || isAdmin)) {
                return (
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={
                      reabrindoProcessoId === processoDetalheId ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <ReplayIcon />
                      )
                    }
                    disabled={Boolean(reabrindoProcessoId)}
                    onClick={() => pDetalhe && handleSolicitarReabrirProcesso(pDetalhe)}
                    sx={{ textTransform: 'none', fontWeight: 800 }}
                  >
                    Reabrir processo
                  </Button>
                );
              }
              return (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    if (pDetalhe) handleAbrirDialogMover(pDetalhe);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    background: 'linear-gradient(90deg, #1d4ed8, #0d9488)',
                  }}
                >
                  Atualizar processo
                </Button>
              );
            })()}
            <IconButton
              onClick={() => { setProcessoDetalheId(null); setTimelineData(null); setEtapaDetalheOpen(false); }}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'rgba(248,250,252,0.7)' }}>
          {loadingTimeline ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : timelineData?.timeline ? (
            <Box sx={{ pl: 0 }}>
              {/* Gráfico: tempo por etapa vs prazo + tempo total */}
              {timelineData.timeline.length > 0 && (() => {
                const useHoras = timelineData.timeline.some((e) => (e.duracaoHoras != null && e.duracaoHoras < 24) || (e.duracaoDias != null && e.duracaoDias < 1)) || (timelineData.tempoTotalHoras != null && timelineData.tempoTotalHoras < 24);
                const chartData = timelineData.timeline.map((e) => ({
                  nome: e.nome?.length > 16 ? e.nome.slice(0, 15) + '…' : e.nome,
                  tempo: useHoras ? (e.duracaoHoras != null ? e.duracaoHoras : (e.duracaoDias != null ? e.duracaoDias * 24 : 0)) : (e.duracaoDias != null ? e.duracaoDias : 0),
                  prazo: useHoras ? (e.prazoDias != null ? Number(e.prazoDias) * 24 : 0) : (e.prazoDias != null ? Number(e.prazoDias) : 0)
                }));
                if (timelineData.tempoTotalDias != null || timelineData.tempoTotalHoras != null) {
                  chartData.push({
                    nome: 'Total processo',
                    tempo: useHoras ? (timelineData.tempoTotalHoras != null ? timelineData.tempoTotalHoras : (timelineData.tempoTotalDias || 0) * 24) : (timelineData.tempoTotalDias || 0),
                    prazo: 0
                  });
                }
                return (
                  <Paper
                    elevation={3}
                    sx={{
                      mb: 3,
                      p: { xs: 2, md: 3 },
                      borderRadius: 3,
                      background: 'linear-gradient(165deg, #0b1f24 0%, #102a30 45%, #0d2228 100%)',
                      border: '1px solid rgba(20,184,166,0.22)',
                      boxShadow: '0 12px 40px rgba(13, 34, 40, 0.35)'
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'rgba(204,251,241,0.85)', letterSpacing: 0.4, mb: 1.5, fontWeight: 700 }}
                    >
                      Tempo gasto por etapa {useHoras ? '(horas)' : '(dias)'} vs prazo configurado
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 80 }}>
                        <defs>
                          <linearGradient id={`hudTempoGrad-${processoDetalheId || 'x'}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.95} />
                            <stop offset="100%" stopColor="#0f766e" stopOpacity={0.8} />
                          </linearGradient>
                          <linearGradient id={`hudPrazoGrad-${processoDetalheId || 'x'}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.7} />
                            <stop offset="100%" stopColor="#64748b" stopOpacity={0.45} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.1} />
                        <XAxis
                          dataKey="nome"
                          angle={-40}
                          textAnchor="end"
                          interval={0}
                          tick={{ fontSize: 10, fill: '#99f6e4' }}
                          axisLine={{ stroke: 'rgba(153,246,228,0.25)' }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={true}
                          label={{
                            value: useHoras ? 'Horas' : 'Dias',
                            angle: -90,
                            position: 'insideLeft',
                            fill: '#99f6e4',
                            fontSize: 11
                          }}
                          tick={{ fontSize: 11, fill: '#99f6e4' }}
                          axisLine={{ stroke: 'rgba(153,246,228,0.25)' }}
                          tickLine={false}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            background: '#1e1e1e',
                            border: '1px solid rgba(45,212,191,0.35)',
                            borderRadius: 10,
                            color: '#f5f5f5',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.45)'
                          }}
                          labelStyle={{ color: '#ccfbf1', fontWeight: 700 }}
                          itemStyle={{ color: '#e2e8f0' }}
                          formatter={(value, name) => [value, name === 'tempo' ? 'Tempo na etapa' : 'Prazo (config.)']}
                        />
                        <Legend wrapperStyle={{ color: '#ccfbf1' }} />
                        <Bar
                          dataKey="tempo"
                          name="Tempo na etapa"
                          fill={`url(#hudTempoGrad-${processoDetalheId || 'x'})`}
                          radius={[6, 6, 0, 0]}
                        />
                        <Bar
                          dataKey="prazo"
                          name="Prazo (config.)"
                          fill={`url(#hudPrazoGrad-${processoDetalheId || 'x'})`}
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                );
              })()}

              <Typography variant="subtitle2" fontWeight={800} color="primary" sx={{ mb: 1.5 }}>
                Timeline das etapas
              </Typography>
              <ProcessoHistoricoTimeline
                timeline={timelineData.timeline}
                tempoTotalDias={timelineData.tempoTotalDias}
                tempoTotalHoras={timelineData.tempoTotalHoras}
                onEtapaClick={(etapa) => {
                  setEtapaDetalheOpen(true);
                  setEtapaDetalheData(null);
                  setLoadingEtapaDetalhe(true);
                  apiFetch(`/api/contratos/processos/${processoDetalheId}/etapas/${etapa.id}/detalhe`)
                    .then((r) => (r.ok ? r.json() : null))
                    .then((d) => { setEtapaDetalheData(d); setLoadingEtapaDetalhe(false); })
                    .catch(() => setLoadingEtapaDetalhe(false));
                }}
                onArquivoClick={(arq) => {
                  const url =
                    arq.url ||
                    arq.arquivoUrl ||
                    (arq.arquivoPath
                      ? `${API_CONFIG.BACKEND_URL}/${String(arq.arquivoPath).replace(/^\/+/, '')}`
                      : null);
                  if (url) openAuthenticatedUpload(url);
                }}
              />

              {processoDetalheId && (() => {
                const pDet = findProcesso(processoDetalheId);
                const tipoAcao = String(pDet?.etapaAtual?.tipoAcao || '').toUpperCase();
                const mostrarEdital =
                  tipoAcao === 'CONFIGURACAO_EDITAL' ||
                  etapaExigeConfiguracaoEdital(pDet?.etapaAtual) ||
                  !!pDet?.editalAtual ||
                  !!pDet?.isEditalPublico;
                const mostrarMapa =
                  tipoAcao === 'MAPA_COMPARATIVO' ||
                  !!pDet?.mapaComparativoConcluido ||
                  !!pDet?.editalAtual ||
                  !!pDet?.isEditalPublico;
                const mostrarAprovacao = ![
                  'CONFIGURACAO_EDITAL',
                  'MAPA_COMPARATIVO',
                  'MINUTA_CLAUSULAS',
                  'PREENCHIMENTO_FORMULARIO',
                ].includes(tipoAcao);

                const abrirMoverComDecisao = (decisao) => {
                  if (!pDet) return;
                  setProcessoSelecionado(pDet);
                  setFluxoMoverEtapas(resolverFluxoDoProcesso(pDet));
                  setFormMover({
                    ...initialMoveForm,
                    acao: decisao.acao === 'REJEITAR' ? 'REJEITAR' : 'APROVAR',
                    observacao: decisao.observacao || '',
                  });
                  setDialogMoverOpen(true);
                  setSnackbar({
                    open: true,
                    message:
                      decisao.acao === 'REJEITAR'
                        ? 'Decisão salva. Confirme a rejeição/devolução no atualizar processo.'
                        : 'Decisão salva. Confirme o avanço no atualizar processo.',
                    severity: 'info',
                  });
                };

                return (
                  <Box sx={{ mt: 3 }}>
                    {mostrarAprovacao && (
                      <PainelEtapaAprovacaoFluxo
                        processoId={processoDetalheId}
                        tipoAcao={tipoAcao || 'APROVACAO_COM_ASSINATURA'}
                        tituloEtapa={pDet?.etapaAtual?.nome || ''}
                        dadosSolicitante={{
                          codigo: pDet?.codigo,
                          titulo: pDet?.titulo,
                          unidade: pDet?.unidade,
                        }}
                        onSaved={() => refreshProcessosAposMutacao()}
                        onDecisao={abrirMoverComDecisao}
                      />
                    )}

                    {tipoAcao === 'MINUTA_CLAUSULAS' && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => abrirMinutaProcesso(pDet)}
                          sx={{ textTransform: 'none', fontWeight: 800 }}
                        >
                          Abrir editor de minuta
                        </Button>
                      </Box>
                    )}

                    {mostrarEdital && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
                          Edital de contratação
                        </Typography>
                        <PainelEditalContratacao
                          processoId={processoDetalheId}
                          limiteReaberturas={pDet?.etapaAtual?.limiteReaberturas || 3}
                          prazoAlertaDias={pDet?.etapaAtual?.prazoAlertaDias || 3}
                          confirmLabel="Salvar edital"
                          onSaved={() => refreshProcessosAposMutacao()}
                        />
                      </Box>
                    )}

                    {mostrarMapa && (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 3,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={700} color="primary">
                            Mapa comparativo de propostas
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<CompareArrowsIcon />}
                            onClick={() =>
                              navigate(`/contratos/analise-comparativa?processoId=${processoDetalheId}`)
                            }
                            sx={{ textTransform: 'none' }}
                          >
                            Benchmark multiunidade
                          </Button>
                        </Box>
                        <MapaComparativoPropostasPanel
                          processoId={processoDetalheId}
                          onConcluido={() => refreshProcessosAposMutacao()}
                        />
                      </>
                    )}
                  </Box>
                );
              })()}
            </Box>
          ) : (
            <Typography color="text.secondary">Selecione um processo na lista para ver a timeline.</Typography>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={etapaDetalheOpen}
        onClose={() => setEtapaDetalheOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(15,23,42,0.28)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background:
              'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,64,175,0.88) 60%, rgba(13,148,136,0.8) 100%)',
            color: '#e2e8f0',
            py: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff' }}>
            Detalhe da etapa
          </Typography>
          {etapaDetalheData?.etapaNome && (
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.5, fontWeight: 600 }}>
              {etapaDetalheData.etapaNome}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc', pt: 2.5 }}>
          {loadingEtapaDetalhe ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
          ) : etapaDetalheData ? (
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 4px 14px rgba(15,23,42,0.04)',
                }}
              >
                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                  CHECKLIST
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {Array.isArray(etapaDetalheData.checklist) && etapaDetalheData.checklist.length > 0
                    ? etapaDetalheData.checklist.map((c) => (typeof c === 'object' && c.label ? c.label : c)).join(', ') || 'Nenhum item'
                    : 'Nenhum item'}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 4px 14px rgba(15,23,42,0.04)',
                }}
              >
                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                  DOCUMENTOS DA ETAPA
                </Typography>
                {Array.isArray(etapaDetalheData.arquivos) && etapaDetalheData.arquivos.length > 0 ? (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {etapaDetalheData.arquivos.map((arq) => {
                      const url =
                        arq.url ||
                        arq.arquivoUrl ||
                        (arq.arquivoPath
                          ? `${API_CONFIG.BACKEND_URL}/${String(arq.arquivoPath).replace(/^\/+/, '')}`
                          : null);
                      if (!url) return null;
                      const tipoLabel =
                        arq.tipo === 'BUNDLE'
                          ? 'Consolidado'
                          : arq.tipo === 'FINAL'
                            ? 'Final'
                            : arq.tipo === 'ORIGINAL'
                              ? 'Original'
                              : arq.tipo || 'PDF';
                      return (
                        <Chip
                          key={arq.id || url}
                          icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
                          label={`${tipoLabel}: ${arq.nome || 'Documento'}`}
                          onClick={() => openAuthenticatedUpload(url)}
                          sx={{
                            justifyContent: 'flex-start',
                            height: 36,
                            bgcolor: 'rgba(14,165,233,0.08)',
                            fontWeight: 600,
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                          }}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    {Array.isArray(etapaDetalheData.documentos) && etapaDetalheData.documentos.length > 0
                      ? `${etapaDetalheData.documentos.length} documento(s)`
                      : 'Nenhum documento anexado nesta etapa'}
                  </Typography>
                )}
              </Box>
              {etapaDetalheData.parecer && (
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    bgcolor: 'rgba(13,148,136,0.06)',
                    border: '1px solid rgba(13,148,136,0.2)',
                  }}
                >
                  <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                    PARECER
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>{etapaDetalheData.parecer}</Typography>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, bgcolor: '#fff' }}>
          <Button onClick={() => setEtapaDetalheOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Fechar
          </Button>
          {processoDetalheId && (
            <Button
              variant="contained"
              onClick={() => {
                const p = findProcesso(processoDetalheId);
                if (p) handleAbrirDialogMover(p);
                setEtapaDetalheOpen(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                background: 'linear-gradient(90deg, #1d4ed8, #0d9488)',
              }}
            >
              Atualizar processo
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Fase 4 — Painel de Edital (texto TR + datas + reabertura; intercepta avanço) */}
      <Dialog
        open={dialogEditalPublicoOpen}
        onClose={() => {
          if (!saving) {
            setDialogEditalPublicoOpen(false);
            setFormEditalPublico(initialEditalPublicoForm);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configuração de Edital de Contratação</DialogTitle>
        <DialogContent dividers>
          {processoSelecionado?.id ? (
            <PainelEditalContratacao
              processoId={processoSelecionado.id}
              limiteReaberturas={
                processoSelecionado?.etapaAtual?.limiteReaberturas ||
                fluxoMoverEtapas?.etapas?.find((e) => e.exigeConfiguracaoEdital)?.limiteReaberturas ||
                3
              }
              prazoAlertaDias={
                processoSelecionado?.etapaAtual?.prazoAlertaDias ||
                fluxoMoverEtapas?.etapas?.find((e) => e.exigeConfiguracaoEdital)?.prazoAlertaDias ||
                3
              }
              disabled={saving}
              confirmLabel={saving ? 'Publicando...' : 'Salvar e avançar etapa'}
              onCancel={() => {
                if (!saving) {
                  setDialogEditalPublicoOpen(false);
                  setFormEditalPublico(initialEditalPublicoForm);
                }
              }}
              onConfirm={async (cfg) => {
                setFormEditalPublico({
                  dataAbertura: cfg.dataAbertura,
                  dataEncerramento: cfg.dataEncerramento,
                  modoPublico: cfg.modoPublico !== false,
                });
                await handleConfirmarEditalPublico(cfg);
              }}
            />
          ) : (
            <Alert severity="warning">Selecione um processo para configurar o edital.</Alert>
          )}
        </DialogContent>
      </Dialog>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(21,101,192,0.16) 0%, rgba(144,202,249,0.22) 100%)',
          border: '1px solid rgba(21,101,192,0.12)',
          boxShadow: '0 20px 40px rgba(21,101,192,0.18)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, value) => setTabValue(value)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              color: 'rgba(13, 71, 161, 0.7)'
            },
            '& .Mui-selected': {
              color: '#0d47a1 !important'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0d47a1',
              height: 3,
              borderRadius: 3
            }
          }}
        >
          <Tab disableRipple value="lista" label="Lista de processos" />
          <Tab disableRipple value="processos" label="Indicadores" />
          <Tab disableRipple value="fluxos" label="Fluxos" />
        </Tabs>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          {podeGerenciarProcessos && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialogProcesso}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Novo Processo
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => navigate('/contratos/solicitacoes')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Solicitações
          </Button>
          <Button
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            onClick={() => navigate(processoDetalheId ? `/contratos/analise-comparativa?processoId=${processoDetalheId}` : '/contratos/analise-comparativa')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Análise Comparativa
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() =>
              navigate('/contratos/atividades', {
                state: processoDetalheId ? { highlightProcessoId: processoDetalheId } : undefined,
              })
            }
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Atividades
          </Button>
          {podeConfigurarFluxo && (
            <Tooltip title="Configurar fluxo / etapas">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => setDialogConfiguracaoOpen(true)}
                  disabled={!fluxoSelecionado}
                  sx={{
                    border: '1px solid rgba(21,101,192,0.25)',
                    borderRadius: 2,
                    bgcolor: 'rgba(21,101,192,0.04)',
                    '&:hover': { bgcolor: 'rgba(21,101,192,0.1)' }
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={carregarProcessos} color="primary" disabled={loadingProcessos}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {tabValue === 'lista' ? (
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(15,23,42,0.08)',
            background: 'rgba(255,255,255,0.97)',
            mb: 3,
            boxShadow: '0 12px 36px rgba(15,23,42,0.06)',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              background:
                'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,64,175,0.88) 55%, rgba(13,148,136,0.8) 100%)',
              color: '#e2e8f0',
            }}
          >
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              Lista de processos
            </Typography>
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.5 }}>
              Visão operacional · filtre, abra o detalhe e avance o fluxo
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Tipo de fluxo</InputLabel>
              <Select
                value={filtroTipoFluxo}
                label="Tipo de fluxo"
                onChange={(e) => {
                  setFiltroTipoFluxo(e.target.value);
                  setFiltroEtapaId('');
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="CONTRATACAO">Contratação</MenuItem>
                <MenuItem value="ADITIVO">Aditivo</MenuItem>
                <MenuItem value="MINUTA">Minuta</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Etapa atual</InputLabel>
              <Select value={filtroEtapaId} label="Etapa atual" onChange={(e) => setFiltroEtapaId(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {etapasFiltroLista.map((et) => (
                  <MenuItem key={et.id} value={et.id}>{et.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filtroStatus} label="Status" onChange={(e) => setFiltroStatus(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={carregarProcessosLista}
              disabled={loadingLista}
            >
              Atualizar
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              disabled={!processosLista.length}
              onClick={() => {
                const n = exportProcessosPdf(processosLista, {
                  label: 'Lista',
                  unidade: unidadeSelecionada?.codigo || '',
                });
                setSnackbar({
                  open: true,
                  message: n ? `PDF gerado (${n} registro(s)).` : 'Nenhum registro para exportar.',
                  severity: n ? 'success' : 'warning',
                });
              }}
              sx={{ textTransform: 'none' }}
            >
              PDF
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!processosLista.length}
              onClick={() => {
                const n = exportProcessosExcel(processosLista, { label: 'Lista' });
                setSnackbar({
                  open: true,
                  message: n ? `Excel gerado (${n} registro(s)).` : 'Nenhum registro para exportar.',
                  severity: n ? 'success' : 'warning',
                });
              }}
              sx={{ textTransform: 'none' }}
            >
              Excel
            </Button>
          </Stack>
          {loadingLista ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <TableContainer
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  '& .MuiTableCell-root': {
                    borderRight: 'none',
                    borderBottomColor: 'rgba(15, 23, 42, 0.06)'
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    bgcolor: 'rgba(15, 23, 42, 0.04)',
                    color: '#0f766e',
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    letterSpacing: 0.5,
                    textTransform: 'uppercase'
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Nº</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Unidade</TableCell>
                    <TableCell>Prestador</TableCell>
                    <TableCell>Etapa atual</TableCell>
                    <TableCell>Responsável</TableCell>
                    <TableCell>Tempo na etapa</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processosLista.map((p) => {
                    const isTerminal = TERMINAL_STATUSES.includes(p.status);
                    const entrada = p.etapaEntradaEm ? new Date(p.etapaEntradaEm) : null;
                    const diasNaEtapa =
                      !isTerminal && entrada && !Number.isNaN(entrada.getTime())
                        ? Math.floor((Date.now() - entrada.getTime()) / (1000 * 60 * 60 * 24))
                        : null;
                    const prazoEtapa = p.etapaAtual?.prazoDias != null ? Number(p.etapaAtual.prazoDias) : null;
                    const estagnado =
                      !isTerminal &&
                      diasNaEtapa != null &&
                      ((prazoEtapa != null && diasNaEtapa > prazoEtapa) ||
                        (prazoEtapa == null && diasNaEtapa >= 7));
                    const alertaCritico = !isTerminal && diasNaEtapa != null && diasNaEtapa >= 14;
                    const dataFinalizado = formatDateShort(p.dataFim || p.updatedAt);
                    const permEtapa = avaliarPermissaoEtapaProcesso({
                      user,
                      etapa: p.etapaAtual,
                      podeGerenciarProcessos,
                      usuarios,
                    });
                    return (
                      <TableRow
                        key={p.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          '&:hover': { bgcolor: 'rgba(20, 184, 166, 0.06)' }
                        }}
                        onClick={() => {
                        setProcessoDetalheId(p.id);
                        setTimelineData(null);
                        setLoadingTimeline(true);
                        apiFetch(`/api/contratos/processos/${p.id}/timeline`)
                          .then((r) => r.ok ? r.json() : null)
                          .then((data) => { setTimelineData(data); setLoadingTimeline(false); })
                          .catch(() => setLoadingTimeline(false));
                        if (String(p.tipoFluxo || '').toUpperCase() === 'MINUTA') {
                          abrirMinutaProcesso(p);
                        }
                      }}>
                        <TableCell sx={{ fontWeight: 700 }}>{p.codigo || p.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={p.tipoFluxo === 'MINUTA' ? 'Minuta' : p.tipoFluxo === 'ADITIVO' ? 'Aditivo' : 'Contratação'}
                            size="small"
                            variant="outlined"
                            color={p.tipoFluxo === 'MINUTA' ? 'secondary' : p.tipoFluxo === 'ADITIVO' ? 'success' : 'primary'}
                            sx={{ borderRadius: 1.5, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={p.unidade || unidadeSelecionada?.codigo || '—'}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 600,
                              color: '#0f766e',
                              borderColor: 'rgba(15,118,110,0.35)',
                              bgcolor: 'rgba(20,184,166,0.06)'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {p.prestador?.empresa || p.prestador?.numeroContrato ? (
                            <Tooltip
                              title={[p.prestador?.empresa, p.prestador?.numeroContrato]
                                .filter(Boolean)
                                .join(' · ')}
                            >
                              <Typography variant="body2" noWrap sx={{ maxWidth: 160, fontWeight: 600 }}>
                                {p.prestador.empresa || p.prestador.numeroContrato}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>{p.etapaAtual?.nome || '—'}</TableCell>
                        <TableCell>{p.responsavelAtual || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            {isTerminal ? (
                              <Typography variant="body2" component="span" color="text.secondary">
                                {dataFinalizado ? `Finalizado em ${dataFinalizado}` : 'Finalizado'}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="body2" component="span">
                                  {diasNaEtapa != null ? `${diasNaEtapa} dia(s)` : '—'}
                                </Typography>
                                {estagnado && (
                                  <Tooltip
                                    title={
                                      prazoEtapa != null
                                        ? `Acima do prazo da etapa (${prazoEtapa} dia(s))`
                                        : 'Processo estagnado nesta etapa'
                                    }
                                  >
                                    <WarningAmberIcon
                                      fontSize="small"
                                      sx={{ color: alertaCritico ? 'error.main' : 'warning.main' }}
                                    />
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1} onClick={(e) => e.stopPropagation()}>
                            <Chip
                              label={STATUS_LABELS[p.status] || p.status}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 600,
                                bgcolor: `${STATUS_COLORS[p.status] || '#9e9e9e'}14`,
                                color: STATUS_COLORS[p.status] || '#616161',
                                borderColor: `${STATUS_COLORS[p.status] || '#9e9e9e'}66`
                              }}
                            />
                            {p.status === 'CONCLUIDO' && p.documentoFinalUrl && (
                              <Tooltip title="Baixar documento consolidado (PDF com minuta)">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => { e.stopPropagation(); openAuthenticatedUpload(p.documentoFinalUrl); }}
                                  sx={{ p: 0.5 }}
                                >
                                  <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <ProcessoRowActions
                            processo={p}
                            isAdmin={isAdmin}
                            excluindo={excluindoProcessoId === p.id}
                            reabrindo={reabrindoProcessoId === p.id}
                            podeAtualizar={permEtapa.canAct}
                            podeReabrir={podeGerenciarProcessos || isAdmin}
                            mensagemSemPermissao={permEtapa.mensagem || ''}
                            onAbrir={(proc) => {
                              setProcessoDetalheId(proc.id);
                              setTimelineData(null);
                              setLoadingTimeline(true);
                              apiFetch(`/api/contratos/processos/${proc.id}/timeline`)
                                .then((r) => (r.ok ? r.json() : null))
                                .then((data) => { setTimelineData(data); setLoadingTimeline(false); })
                                .catch(() => setLoadingTimeline(false));
                            }}
                            onAtualizar={(proc) => handleAbrirDialogMover(proc)}
                            onBaixarPdf={(proc) => openAuthenticatedUpload(proc.documentoFinalUrl)}
                            onAnalise={(proc) => navigate(`/contratos/analise-comparativa?processoId=${proc.id}`)}
                            onExcluir={(proc) => handleSolicitarExcluirProcesso(proc)}
                            onReabrir={(proc) => handleSolicitarReabrirProcesso(proc)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {processosLista.length === 0 && !loadingLista && (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nenhum processo encontrado.</Typography>
          )}
          </Box>
        </Paper>
      ) : tabValue === 'processos' ? (
        <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid rgba(21,101,192,0.08)',
          background: 'rgba(255,255,255,0.9)'
        }}
      >
        <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
            <Typography variant="h6" fontWeight={800} color="primary">
              Indicadores do processo de contratação
            </Typography>
            {loadingFluxos ? (
              <CircularProgress size={20} sx={{ color: 'primary.main' }} />
            ) : (
              <FormControl size="small" sx={{ minWidth: 240, backgroundColor: 'white', borderRadius: 2 }}>
                <InputLabel>Fluxo (só indicadores)</InputLabel>
                <Select
                  value={fluxoSelecionado}
                  label="Fluxo (só indicadores)"
                  onChange={(event) => setFluxoSelecionado(event.target.value)}
                >
                  {fluxos.map((fluxo) => (
                    <MenuItem key={fluxo.id} value={fluxo.id}>
                      {fluxo.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Chip
            label={`${processosIndicadores.length} processo(s) monitorados`}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: '#1565c0',
              borderColor: 'rgba(21,101,192,0.4)'
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          KPIs apenas do fluxo selecionado. A aba Lista de processos permanece independente.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2.5}>
          {resumoProcessos.map((card) => (
            <Grid item xs={12} sm={6} md={3} lg={3} key={card.titulo}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: card.fundo,
                  border: `1px solid ${card.cor}33`
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: card.cor, fontWeight: 700, textTransform: 'uppercase' }}>
                      {card.titulo}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: '#0d1b2a' }}>
                      {card.valor}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: `${card.cor}1a`,
                      color: card.cor,
                      display: 'flex'
                    }}
                  >
                    {card.icone}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(21,101,192,0.08)',
              background: 'rgba(255,255,255,0.95)',
              mb: 3
            }}
          >
            <Box
              sx={{
                p: 3,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, rgba(21,101,192,0.06) 0%, rgba(21,101,192,0.02) 100%)',
                borderBottom: '1px solid rgba(21,101,192,0.08)',
                borderRadius: 'inherit'
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={800} color="primary">
                  Selecione o fluxo para visualizar o fluxograma
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  O diagrama abaixo ilustra cada etapa do processo e a quantidade de processos em andamento.
                </Typography>
              </Box>
              {loadingFluxos ? (
                <Box display="flex" alignItems="center" gap={1} sx={{ color: 'primary.main' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Carregando fluxos...</Typography>
                </Box>
              ) : (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 260 }, backgroundColor: 'white', borderRadius: 2 }}>
                  <InputLabel>Fluxo</InputLabel>
                  <Select
                    value={fluxoSelecionado}
                    label="Fluxo"
                    onChange={(event) => setFluxoSelecionado(event.target.value)}
                  >
                    {fluxos.map((fluxo) => (
                      <MenuItem key={fluxo.id} value={fluxo.id}>
                        {fluxo.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(21,101,192,0.08)',
              background: 'rgba(255,255,255,0.95)'
            }}
          >
            <Box sx={{ p: 3 }}>
              {loadingIndicadores ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                  <CircularProgress />
                </Box>
              ) : !fluxoAtual ? (
                <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhum fluxo configurado ainda.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure um fluxo padrão para visualizar o fluxograma do processo de contratação.
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={3}>
                  <Box
                    display="flex"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    gap={2}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="primary">
                        Fluxograma do processo
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {visualizacaoFluxo === 'agregado' 
                          ? 'Visualização agregada: mostra todos os processos e onde cada um está posicionado.'
                          : 'Visualização individual: mostra a posição de um processo específico no fluxo.'}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadFluxograma}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 3,
                          fontWeight: 600
                        }}
                      >
                        Baixar fluxograma
                      </Button>
                    </Stack>
                  </Box>
                  <Divider />
                  <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(227,242,253,0.3)', borderRadius: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Tipo de Visualização</InputLabel>
                        <Select
                          value={visualizacaoFluxo}
                          label="Tipo de Visualização"
                          onChange={(e) => {
                            setVisualizacaoFluxo(e.target.value);
                            if (e.target.value === 'agregado') {
                              setProcessoFluxoSelecionado(null);
                            }
                          }}
                        >
                          <MenuItem value="agregado">Agregado (Todos os processos)</MenuItem>
                          <MenuItem value="individual">Individual (Processo específico)</MenuItem>
                        </Select>
                      </FormControl>
                      {visualizacaoFluxo === 'individual' && (
                        <FormControl size="small" sx={{ minWidth: 300 }}>
                          <InputLabel>Selecione o Processo</InputLabel>
                          <Select
                            value={processoFluxoSelecionado?.id || ''}
                            label="Selecione o Processo"
                            onChange={(e) => {
                              const processo = processosIndicadores.find(p => p.id === e.target.value);
                              setProcessoFluxoSelecionado(processo || null);
                            }}
                          >
                            <MenuItem value="">Nenhum selecionado</MenuItem>
                            {processosIndicadores.map((processo) => (
                              <MenuItem key={processo.id} value={processo.id}>
                                {processo.codigo} - {processo.titulo}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {visualizacaoFluxo === 'agregado' && (
                        <Chip
                          label={`Total: ${processosIndicadores.length} processo(s) monitorado(s)`}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {visualizacaoFluxo === 'individual' && processoFluxoSelecionado && (
                        <Chip
                          label={`Processo: ${processoFluxoSelecionado.codigo}`}
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Box>
                  <Box sx={{ width: '100%', overflowX: 'auto', pb: 2 }}>
                    <FluxogramaVisualCompleto 
                      ref={fluxogramaRef} 
                      fluxo={fluxoAtual} 
                      contagemEtapas={contagemEtapas}
                      processos={visualizacaoFluxo === 'agregado' ? processosIndicadores : []}
                      processoSelecionado={visualizacaoFluxo === 'individual' ? processoFluxoSelecionado : null}
                    />
                  </Box>
                </Stack>
              )}
            </Box>
          </Paper>
        </>
      )}

      <Dialog
        open={dialogConfiguracaoOpen}
        onClose={() => setDialogConfiguracaoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: HUD_DIALOG_PAPER }}
      >
        <DialogTitle sx={HUD_DIALOG_TITLE}>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
            {(fluxoAtual?.nome || '').toLowerCase().includes('aditivo')
              ? 'Configuração do fluxo de Aditivo'
              : 'Configuração do fluxo selecionado'}
          </Typography>
          {fluxoAtual?.nome && (
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.5, fontWeight: 600 }}>
              {fluxoAtual.nome}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
          {fluxoAtual ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {fluxoAtual.descricao && (
                <Typography variant="body2" color="text.secondary">
                  {fluxoAtual.descricao}
                </Typography>
              )}

              <Divider>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: 1 }}>
                  Etapas configuradas
                </Typography>
              </Divider>

              {Array.isArray(fluxoAtual.etapas) && fluxoAtual.etapas.length > 0 ? (
                fluxoAtual.etapas.map((etapa, index) => (
                  <Box
                    key={etapa.id || index}
                    sx={{
                      ...HUD_CARD,
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr',
                      gap: 1.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={HUD_STEP_NUMBER}>{index + 1}</Box>
                    <Stack spacing={0.75}>
                      <Typography variant="subtitle2" fontWeight={800}>
                        {etapa.nome}
                      </Typography>
                      {etapa.descricao && (
                        <Typography variant="body2" color="text.secondary">
                          {etapa.descricao}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {etapa.grupo_responsavel && (
                          <Chip
                            label={`Responsável: ${etapa.grupo_responsavel}`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(14,165,233,0.1)', color: '#0369a1' }}
                          />
                        )}
                        {etapa.prazo_dias && (
                          <Chip
                            label={`Prazo: ${etapa.prazo_dias} dia(s)`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(234,88,12,0.1)', color: '#c2410c' }}
                          />
                        )}
                        {etapa.status_resultante && (
                          <Chip
                            label={`Status ao concluir: ${STATUS_LABELS[etapa.status_resultante] || etapa.status_resultante}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor:
                                etapa.status_resultante === 'CONCLUIDO'
                                  ? 'rgba(13,148,136,0.12)'
                                  : 'rgba(15,23,42,0.06)',
                              color: etapa.status_resultante === 'CONCLUIDO' ? '#0f766e' : 'text.secondary',
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma etapa cadastrada para este fluxo.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Selecione um fluxo para visualizar as configurações disponíveis.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={abrirEdicaoFluxo}
              sx={{ ...HUD_CTA_BUTTON, mr: 0 }}
            >
              {(fluxoAtual?.nome || '').toLowerCase().includes('aditivo') ? 'Editar fluxo de Aditivo' : 'Editar fluxo'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setDialogEditarTROpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
            >
              Editar TR
            </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setDialogEditarCIOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed' }}
          >
            Editar CI
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setDialogEditarNotificacaoOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(124,58,237,0.45)', color: '#7c3aed', mr: 'auto' }}
          >
            Editar Notificação
          </Button>
          <Button onClick={() => setDialogConfiguracaoOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <EditorTRModal
        open={dialogEditarTROpen}
        onClose={() => setDialogEditarTROpen(false)}
        fluxoId={fluxoAtual?.id || fluxoSelecionado || null}
        processoId={null}
        escopo="fluxo"
      />
      <EditorMinutaClausulas
        open={dialogMinutaProcesso.open}
        onClose={() => setDialogMinutaProcesso({ open: false, processoId: null, titulo: '' })}
        processoId={dialogMinutaProcesso.processoId}
        readOnly={!podeEditarMinutaProcesso}
        onSaved={() => refreshProcessosAposMutacao()}
        onGerarAssinatura={({ processoId, titulo }) => {
          setDialogMinutaProcesso({ open: false, processoId: null, titulo: '' });
          navigate('/assinaturas/novo', {
            state: {
              fromContratos: true,
              processoId,
              titulo: titulo || 'Assinatura — Minuta',
              tipoDocumento: 'MINUTA',
              processoCodigo: findProcesso(processoId)?.codigo || null,
            },
          });
        }}
      />
      <EditorCIModal
        open={dialogEditarCIOpen}
        onClose={() => setDialogEditarCIOpen(false)}
        fluxoId={fluxoAtual?.id || fluxoSelecionado || null}
        processoId={null}
        escopo="fluxo"
      />
      <EditorNotificacaoModal
        open={dialogEditarNotificacaoOpen}
        onClose={() => setDialogEditarNotificacaoOpen(false)}
        fluxoId={fluxoAtual?.id || fluxoSelecionado || null}
        processoId={null}
        escopo="fluxo"
      />

      <Dialog open={dialogProcessoOpen} onClose={handleCloseDialogProcesso} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Processo</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={formProcessoAttempted && !formProcesso.fluxoId}>
                <InputLabel>Fluxo</InputLabel>
                <Select
                  value={formProcesso.fluxoId}
                  label="Fluxo"
                  onChange={(event) => setFormProcesso((prev) => ({ ...prev, fluxoId: event.target.value }))}
                >
                  {fluxos.map((fluxo) => (
                    <MenuItem key={fluxo.id} value={fluxo.id}>
                      {fluxo.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Título do Processo"
                fullWidth
                size="small"
                required
                value={formProcesso.titulo}
                onChange={(event) => setFormProcesso((prev) => ({ ...prev, titulo: event.target.value }))}
                placeholder="Ex: Contratação de serviço de limpeza"
                error={formProcessoAttempted && !formProcesso.titulo.trim()}
                helperText={
                  formProcessoAttempted && !formProcesso.titulo.trim()
                    ? 'Informe o título do processo.'
                    : 'Preferencialmente originado via Solicitações → Enviar Análise.'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={prestadoresOpcoes}
                loading={loadingPrestadores}
                value={prestadorFormSelecionado}
                onChange={(_, val) =>
                  setFormProcesso((prev) => ({ ...prev, prestadorId: val?.id || null }))
                }
                getOptionLabel={(opt) =>
                  opt
                    ? `${opt.empresa || 'Sem nome'}${opt.numeroContrato ? ` · ${opt.numeroContrato}` : ''}`
                    : ''
                }
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={fluxoFormEhAditivo ? 'Vincular a Prestador (obrigatório)' : 'Vincular a Prestador (opcional)'}
                    size="small"
                    error={formProcessoAttempted && fluxoFormEhAditivo && !formProcesso.prestadorId}
                    helperText={
                      formProcessoAttempted && fluxoFormEhAditivo && !formProcesso.prestadorId
                        ? 'Aditivo exige um contrato/prestador existente.'
                        : 'Busque por empresa ou número do contrato.'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição / Observações"
                fullWidth
                multiline
                minRows={4}
                value={formProcesso.descricao}
                onChange={(event) => setFormProcesso((prev) => ({ ...prev, descricao: event.target.value }))}
                placeholder="Detalhe as informações relevantes do processo..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogProcesso} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSalvarProcesso}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Salvando...' : 'Criar Processo'}
          </Button>
        </DialogActions>
      </Dialog>

      <AtualizarProcessoModal
        open={dialogMoverOpen}
        onClose={handleFecharDialogMover}
        processo={processoSelecionado}
        fluxo={fluxoMoverEtapas || resolverFluxoDoProcesso(processoSelecionado)}
        user={user}
        usuarioEmail={usuarioEmail}
        usuarioTipo={usuarioTipo}
        usuarioSetor={usuarioSetor}
        usuarios={usuarios}
        podeGerenciarProcessos={podeGerenciarProcessos}
        podePularEtapas={isAdmin}
        onBeforeSubmit={handleBeforeMoverSubmit}
        onSuccess={handleMoverSuccess}
      />

      <Dialog
        open={Boolean(processoExcluirConfirm)}
        onClose={() => !excluindoProcessoId && setProcessoExcluirConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir processo</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Deseja realmente excluir o processo{' '}
            <strong>{processoExcluirConfirm?.codigo || processoExcluirConfirm?.id}</strong>?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProcessoExcluirConfirm(null)}
            disabled={Boolean(excluindoProcessoId)}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(excluindoProcessoId)}
            onClick={() => handleExcluirProcesso(processoExcluirConfirm)}
            startIcon={excluindoProcessoId ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: 'none' }}
          >
            {excluindoProcessoId ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(processoReabrirConfirm)}
        onClose={() => !reabrindoProcessoId && setProcessoReabrirConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reabrir processo</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Reabrir o processo{' '}
            <strong>{processoReabrirConfirm?.codigo || processoReabrirConfirm?.id}</strong>?
            Ele voltará à primeira etapa com status <strong>Em andamento</strong>. O histórico da
            rejeição será preservado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProcessoReabrirConfirm(null)}
            disabled={Boolean(reabrindoProcessoId)}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={Boolean(reabrindoProcessoId)}
            onClick={() => handleReabrirProcesso(processoReabrirConfirm)}
            startIcon={reabrindoProcessoId ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
            sx={{ textTransform: 'none' }}
          >
            {reabrindoProcessoId ? 'Reabrindo...' : 'Reabrir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Edição de Fluxo */}
      <Dialog
        open={editandoFluxo}
        onClose={fecharEdicaoFluxo}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: HUD_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ ...HUD_DIALOG_TITLE, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              Editar Fluxo
            </Typography>
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.25 }}>
              Monte a sequência de etapas, prazos e responsáveis
            </Typography>
          </Box>
          <IconButton onClick={fecharEdicaoFluxo} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
          {fluxoEditado ? (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Nome do Fluxo"
                    fullWidth
                    value={fluxoEditado.nome}
                    onChange={(e) => setFluxoEditado({ ...fluxoEditado, nome: e.target.value })}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descrição"
                    fullWidth
                    multiline
                    minRows={2}
                    value={fluxoEditado.descricao || ''}
                    onChange={(e) => setFluxoEditado({ ...fluxoEditado, descricao: e.target.value })}
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: 1 }}>
                  Etapas do Fluxo
                </Typography>
              </Divider>

              <Stack spacing={2}>
                {fluxoEditado.etapas.map((etapa, index) => (
                  <Box
                    key={index}
                    sx={{
                      ...HUD_CARD,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={HUD_STEP_NUMBER}>{etapa.ordem || index + 1}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {etapa.nome || 'Etapa sem nome'}
                      </Typography>
                      {etapa.descricao && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {etapa.descricao}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {etapa.grupo && (
                          <Chip
                            label={`Responsável: ${etapa.grupo}`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(14,165,233,0.1)', color: '#0369a1' }}
                          />
                        )}
                        {etapa.prazo && (
                          <Chip
                            label={`Prazo: ${etapa.prazo} dia(s)`}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: 'rgba(234,88,12,0.1)', color: '#c2410c' }}
                          />
                        )}
                        <Chip
                          label={`Status: ${STATUS_LABELS[etapa.status] || etapa.status}`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor:
                              etapa.status === 'CONCLUIDO'
                                ? 'rgba(13,148,136,0.12)'
                                : 'rgba(15,23,42,0.06)',
                            color: etapa.status === 'CONCLUIDO' ? '#0f766e' : 'text.secondary',
                          }}
                        />
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Mover para cima">
                        <IconButton
                          size="small"
                          onClick={() => moverEtapa(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mover para baixo">
                        <IconButton
                          size="small"
                          onClick={() => moverEtapa(index, 'down')}
                          disabled={index === fluxoEditado.etapas.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar etapa">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => editarEtapa(etapa, index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover etapa">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removerEtapa(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={adicionarEtapa}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  py: 1.25,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: 'rgba(13,148,136,0.45)',
                  color: '#0f766e',
                  bgcolor: 'rgba(13,148,136,0.04)',
                }}
              >
                Adicionar Etapa
              </Button>
            </Stack>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75 }}>
          <Button onClick={fecharEdicaoFluxo} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={salvarFluxo}
            disabled={saving}
            sx={HUD_CTA_BUTTON}
          >
            {saving ? 'Salvando...' : 'Salvar Fluxo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Edição de Etapa */}
      <Dialog
        open={dialogEtapaOpen}
        onClose={() => !saving && setDialogEtapaOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid rgba(15,118,110,0.12)',
            boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {etapaEditando?.isNew ? 'Nova Etapa' : 'Editar Etapa'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome da Etapa"
              fullWidth
              value={formEtapa.nome}
              onChange={(e) => setFormEtapa({ ...formEtapa, nome: e.target.value })}
              size="small"
              required
              placeholder="Ex: Elaboração da Minuta"
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              minRows={2}
              value={formEtapa.descricao}
              onChange={(e) => setFormEtapa({ ...formEtapa, descricao: e.target.value })}
              size="small"
              placeholder="Descreva o que acontece nesta etapa"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Setor/Grupo (opcional)</InputLabel>
              <Select
                value={formEtapa.grupo}
                label="Setor/Grupo (opcional)"
                onChange={(e) => setFormEtapa({ ...formEtapa, grupo: e.target.value })}
              >
                <MenuItem value="">Nenhum</MenuItem>
                {loadingSetores ? (
                  <MenuItem disabled>Carregando setores...</MenuItem>
                ) : (
                  (setores || []).map((setor) => (
                    <MenuItem key={setor.id} value={setor.nome}>
                      {setor.nome}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Responsáveis (usuários que podem autorizar/negar)</InputLabel>
              <Select
                multiple
                value={formEtapa.responsaveis}
                onChange={(e) => setFormEtapa({ ...formEtapa, responsaveis: e.target.value })}
                input={<OutlinedInput label="Responsáveis (usuários que podem autorizar/negar)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((userId) => {
                      const usuario = usuarios.find(u => u.id === userId);
                      return (
                        <Chip
                          key={userId}
                          label={usuario ? `${usuario.nome}${usuario.email ? ` (${usuario.email})` : ''}` : `ID: ${userId}`}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {loadingUsuarios ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                    Carregando usuários...
                  </MenuItem>
                ) : (
                  usuarios.map((usuario) => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      <Checkbox checked={formEtapa.responsaveis.indexOf(usuario.id) > -1} />
                      <ListItemText
                        primary={usuario.nome}
                        secondary={usuario.email || usuario.setor || ''}
                      />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Prazo (dias)"
                  type="number"
                  fullWidth
                  value={formEtapa.prazo}
                  onChange={(e) => setFormEtapa({ ...formEtapa, prazo: e.target.value })}
                  size="small"
                  inputProps={{ min: 1 }}
                  placeholder="Ex: 2"
                  helperText="Prazo máximo para conclusão"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Tempo Estimado (dias)"
                  type="number"
                  fullWidth
                  value={formEtapa.tempoEstimado}
                  onChange={(e) => setFormEtapa({ ...formEtapa, tempoEstimado: e.target.value })}
                  size="small"
                  inputProps={{ min: 1 }}
                  placeholder="Ex: 1"
                  helperText="Tempo esperado para esta etapa"
                />
              </Grid>
            </Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Status ao Concluir</InputLabel>
              <Select
                value={formEtapa.status}
                label="Status ao Concluir"
                onChange={(e) => setFormEtapa({ ...formEtapa, status: e.target.value })}
              >
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Cor (opcional)"
              fullWidth
              value={formEtapa.cor}
              onChange={(e) => setFormEtapa({ ...formEtapa, cor: e.target.value })}
              size="small"
              placeholder="Ex: rgba(25,118,210,0.12)"
              helperText="Cor de fundo da etapa no fluxograma"
            />
            <Divider />
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Ações Permitidas
            </Typography>
            <Stack
              spacing={1.25}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(15,118,110,0.04)',
                border: '1px solid rgba(15,118,110,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Permitir Autorizar/Aprovar</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Permite avançar o processo com autorização formal nesta etapa.
                  </Typography>
                </Box>
                <Switch
                  checked={formEtapa.permiteAprovar}
                  onChange={(e) => setFormEtapa({ ...formEtapa, permiteAprovar: e.target.checked })}
                  color="primary"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Permitir Rejeitar</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Permite rejeitar a solicitação nesta etapa do fluxo.
                  </Typography>
                </Box>
                <Switch
                  checked={formEtapa.permiteRejeitar}
                  onChange={(e) => setFormEtapa({ ...formEtapa, permiteRejeitar: e.target.checked })}
                  color="primary"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Permitir Devolver</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Permite que o processo retorne à etapa anterior.
                  </Typography>
                </Box>
                <Switch
                  checked={formEtapa.permiteDevolver}
                  onChange={(e) => setFormEtapa({ ...formEtapa, permiteDevolver: e.target.checked })}
                  color="primary"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Retornar ao início quando rejeitado</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Em caso de rejeição, o processo volta à primeira etapa do fluxo.
                  </Typography>
                </Box>
                <Switch
                  checked={formEtapa.retornaInicio}
                  onChange={(e) => setFormEtapa({ ...formEtapa, retornaInicio: e.target.checked })}
                  color="primary"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Exige configuração de Edital</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ao avançar para esta etapa, solicita datas de abertura/encerramento do edital público.
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(formEtapa.exigeConfiguracaoEdital)}
                  onChange={(e) => setFormEtapa({ ...formEtapa, exigeConfiguracaoEdital: e.target.checked })}
                  color="primary"
                />
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogEtapaOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvarEtapa}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
          >
            {saving ? 'Salvando...' : 'Salvar Etapa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

